import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStudioStore, useAuthStore } from '@/store'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  DashboardData,
  StudioAnalytics,
  StudioIssue,
  ActivityItem,
  TrendData
} from '@/types'
import { Button, Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { ManagementCompanyNavigation } from '@/components'
import { UkWeeklyPlanModal, type UkWeeklyPlanRow } from '@/components/UkWeeklyPlanModal'
import { PeriodSelector } from '@/components/analytics'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { formatCurrency } from '@/lib/utils'
import { getCurrentWeekNetworkFixedCd, type CurrentWeekNetworkFixedCdSnapshot } from '@/services/networkFixedCdService'
import {
  getManagementDashboardNetworkKpis,
  type ManagementDashboardNetworkKpis,
} from '@/services/managementDashboardNetworkKpisService'
import {
  AlertTriangle,
  CheckCircle,
  Building2,
  Calendar,
  FileText,
  Users,
  Globe2,
  Repeat2,
  WalletCards,
  Activity,
  Gauge,
  Pencil,
  Info,
  Link2
} from 'lucide-react'

const MANAGEMENT_BRANCH_PARTNER_NUMBER = 1000

function getInclusiveDays(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00`)
  const endDate = new Date(`${end}T00:00:00`)
  const diff = endDate.getTime() - startDate.getTime()
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)) + 1)
}

function getRangeOverlapRatio(
  sourceStart: string,
  sourceEnd: string,
  targetStart: string,
  targetEnd: string,
  sourceLenDays?: number | null
): number {
  const overlapStart = sourceStart > targetStart ? sourceStart : targetStart
  const overlapEnd = sourceEnd < targetEnd ? sourceEnd : targetEnd
  if (overlapEnd < overlapStart) return 0
  const baseDays = Math.max(1, sourceLenDays || getInclusiveDays(sourceStart, sourceEnd))
  return getInclusiveDays(overlapStart, overlapEnd) / baseDays
}

function getElapsedDaysInRange(start: string, end: string, today: string): number {
  if (today < start) return 0
  if (today > end) return getInclusiveDays(start, end)
  return getInclusiveDays(start, today)
}

function getRemainingDaysInRange(start: string, end: string, today: string): number {
  if (today > end) return 0
  const firstRemainingDay = today < start ? start : today
  return getInclusiveDays(firstRemainingDay, end)
}

function getElapsedRangeEnd(start: string, end: string, today: string): string | null {
  if (today < start) return null
  return today > end ? end : today
}

function toFiniteNumber(value: unknown): number {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

function formatFullCurrency(value: number | null | undefined): string {
  return formatCurrency(toFiniteNumber(value), 'RUB')
}

function formatPercent(value: number | null | undefined): string {
  return `${toFiniteNumber(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  })}%`
}

function formatMetricAverage(value: number | null | undefined): string {
  return toFiniteNumber(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  })
}

function formatSignedCurrency(value: number | null | undefined): string {
  const numericValue = toFiniteNumber(value)
  return `${numericValue >= 0 ? '+' : ''}${formatFullCurrency(numericValue)}`
}

function formatSignedCount(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toLocaleString('ru-RU')}`
}

function FactPlanText({
  fact,
  plan,
  planClassName = 'text-slate-400'
}: {
  fact: ReactNode
  plan: ReactNode
  planClassName?: string
}) {
  return (
    <div className="space-y-1 whitespace-nowrap">
      <div className="font-semibold text-slate-950">{fact}</div>
      <div className={`text-xs font-medium ${planClassName}`}>{plan}</div>
    </div>
  )
}

function RevenueStudioCountCell({ segment }: { segment: ManagementStudioRevenueSegment }) {
  const hasMovement = segment.cameCount > 0 || segment.leftCount > 0

  return (
    <div className="inline-flex flex-col items-end gap-1">
      <div className="font-semibold text-slate-950">
        {segment.studiosAtPeriodEnd.toLocaleString('ru-RU')}
      </div>
      {hasMovement ? (
        <span className="relative inline-flex items-center group cursor-help text-xs font-medium text-slate-500">
          <span>
            {formatSignedCount(segment.cameCount)} / -{segment.leftCount.toLocaleString('ru-RU')}
          </span>
          <span className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden w-64 whitespace-normal rounded bg-foreground px-2 py-1.5 text-[11px] font-normal leading-snug text-background shadow-lg group-hover:block">
            {segment.cameFrom.length > 0 ? (
              <span className="block">
                Пришли: {segment.cameFrom.map((row) => `${row.count} из ${row.label}`).join(', ')}
              </span>
            ) : null}
            {segment.leftTo.length > 0 ? (
              <span className="block">
                Ушли: {segment.leftTo.map((row) => `${row.count} в ${row.label}`).join(', ')}
              </span>
            ) : null}
          </span>
        </span>
      ) : (
        <div className="text-xs font-medium text-slate-400">без изменений</div>
      )}
    </div>
  )
}

function TableMetricHint({
  label,
  hint
}: {
  label: ReactNode
  hint: ReactNode
}) {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      <span>{label}</span>
      <span className="relative inline-flex items-center group cursor-help normal-case">
        <Info className="h-3.5 w-3.5 text-slate-400" />
        <span className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden w-56 whitespace-normal rounded bg-foreground px-2 py-1.5 text-[11px] font-normal leading-snug tracking-normal text-background shadow-lg group-hover:block">
          {hint}
        </span>
      </span>
    </span>
  )
}

function SplitFactPlanText({
  regularFact,
  regularPlan,
  subscriptionFact,
  subscriptionPlan
}: {
  regularFact: ReactNode
  regularPlan: ReactNode
  subscriptionFact: ReactNode
  subscriptionPlan: ReactNode
}) {
  return (
    <div className="space-y-1.5 text-slate-950 whitespace-nowrap">
      <div>
        <div className="font-semibold">{regularFact}</div>
        <div className="text-xs font-medium text-slate-400">{regularPlan}</div>
      </div>
      <div className="border-t border-slate-200 pt-1.5">
        <div className="font-semibold text-amber-700/90">{subscriptionFact}</div>
        <div className="text-xs font-medium text-amber-600/70">{subscriptionPlan}</div>
      </div>
    </div>
  )
}

type RevenueSegmentKey = 'all' | 'regular' | 'subscription' | 'mixed' | 'foreign'
type StudioSubscriptionKind = 'REGULAR' | 'SUBSCRIPTION' | 'MIXED'
type RangeState = 'past' | 'current' | 'future'
type GeographySegmentKey = 'russia' | 'near_abroad' | 'other_foreign'
type LeadPaceStatus = 'good' | 'warning' | 'bad' | 'neutral'

interface RevenueSegmentAccumulator {
  key: RevenueSegmentKey
  label: string
  description: string
  revenue: number
  excludedRevenue: number
  studioDays: Set<string>
  excludedStudioDays: Set<string>
  studios: Set<number>
  endStudios: Set<number>
  cameFrom: Map<string, number>
  leftTo: Map<string, number>
}

interface SubscriptionHistoryRow {
  studio_internal_id: number
  old_subscription_type: StudioSubscriptionKind | null
  new_subscription_type: StudioSubscriptionKind | null
  changed_at: string | null
}

type StudioStatusCode = 'PRE-OPEN' | 'OPEN' | 'CLOSED' | 'SERVICE' | 'SUSPENDED' | string

interface StudioStatusHistoryRow {
  studio_internal_id: number
  old_status_code: StudioStatusCode | null
  new_status_code: StudioStatusCode | null
  changed_at: string | null
}

interface CityGeographyRow {
  city_id: number
  country: string | null
  city_land_group: number | null
}

interface WeeklyPlanDetailRow {
  studio_internal_id: number
  week_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number | null
  weekly_gross_revenue_plan: number | null
  weekly_marketing_budget_plan: number | null
  weekly_leads_plan: number | null
  weekly_appointments_plan: number | null
  weekly_new_visitors_plan: number | null
  weekly_new_sales_plan: number | null
  weekly_new_sales_amount_plan: number | null
  weekly_new_subscriptions_count_plan: number | null
  weekly_new_subscriptions_amount_plan: number | null
  weekly_membership_fees_new_plan: number | null
}

interface ManagementCompanyStudioRow {
  studio_internal_id: number
  studio_number: string
  city_id: number
  city_name?: string | null
  address?: string | null
  number_of_halls?: number | null
  area?: number | null
  created_at: string
  updated_at: string
  status_code?: string | null
  access_kind?: string | null
  team_role?: string | null
  // RPC отдаёт номер партнёра числом, но фильтр ниже уже защищается от строки/пустой строки —
  // тип обязан это допускать, иначе сравнение с '' не компилируется.
  partner_number?: number | string | null
  currency_code?: string | null
}

interface DashboardSubscriberStats {
  totalSubscribers: number
  subscribersGrowth: number
}

interface ManagementStudioRevenueSegment {
  key: RevenueSegmentKey
  label: string
  description: string
  revenue: number
  excludedRevenue: number
  workedStudioDays: number
  excludedStudioDays: number
  studiosCount: number
  studiosAtPeriodEnd: number
  cameCount: number
  leftCount: number
  cameFrom: Array<{ label: string; count: number }>
  leftTo: Array<{ label: string; count: number }>
  averagePerStudioDay: number
  averageWeeklyPerStudio: number
  averagePerStudioForPeriod: number
}

interface GeographyDashboardSegment {
  key: GeographySegmentKey
  label: string
  description: string
  studiosCount: number
  marketingStudiosCount: number
  averageMarketingSpendPerStudio: number
  costPerLead: number
  averageLeadsPerStudio: number
  averageAppointmentsPerStudio: number
  averageVisitorsPerStudio: number
  regularStudiosCount: number
  averageRegularSalesPerStudio: number
  regularSalesConversion: number
  appointmentConversion: number
  visitConversion: number
  averageRegularCheck: number
  subscriptionStudiosCount: number
  averageSubscriptionSalesPerStudio: number
  subscriptionSalesConversion: number
  averageSubscriptionCheck: number
  hasSubscriptionBlock: boolean
  planFilledStudiosCount: number
  planAverageMarketingSpendPerStudio: number
  planCostPerLead: number
  planAverageLeadsPerStudio: number
  planAverageAppointmentsPerStudio: number
  planAverageVisitorsPerStudio: number
  planAverageRegularSalesPerStudio: number
  planRegularSalesConversion: number
  planAppointmentConversion: number
  planVisitConversion: number
  planAverageRegularCheck: number
  planAverageSubscriptionSalesPerStudio: number
  planSubscriptionSalesConversion: number
  planAverageSubscriptionCheck: number
  leadPaceStatus: LeadPaceStatus
}

interface MarketingMetricTotals {
  marketingExpenses: number
  marketingLeads: number
  leads: number
  appointments: number
  visitors: number
  regularVisitors: number
  subscriptionVisitors: number
  regularSales: number
  subscriptionSales: number
  regularRevenue: number
  subscriptionRevenue: number
}

interface MarketingFactStudio extends MarketingMetricTotals {
  hasRegularLine: boolean
  hasSubscriptionLine: boolean
}

interface MarketingAlignedSegment {
  baseStudios: Set<number>
  regularStudios: Set<number>
  subscriptionStudios: Set<number>
  fact: MarketingMetricTotals
  plan: MarketingMetricTotals
  leadPaceFact: number
  leadPacePlan: number
}

interface ManagementDashboardData extends Omit<DashboardData, 'studioRevenueSegments'> {
  studioRevenueSegments: ManagementStudioRevenueSegment[]
  geographySegments: GeographyDashboardSegment[]
  subscribers: DashboardSubscriberStats
  appFactToDate: number
  crmFactToDate: number | null
  crmYesterdayRevenue: number
  crmTodayRevenue: number
  planToDate: number
  planToDateRatio: number
  rangeState: RangeState
}

const OPEN_STUDIO_STATUSES = new Set(['OPEN'])
const NEAR_ABROAD_LAND_GROUP_CODE = 12

function createRevenueSegment(
  key: RevenueSegmentKey,
  label: string,
  description: string
): RevenueSegmentAccumulator {
  return {
    key,
    label,
    description,
    revenue: 0,
    excludedRevenue: 0,
    studioDays: new Set<string>(),
    excludedStudioDays: new Set<string>(),
    studios: new Set<number>(),
    endStudios: new Set<number>(),
    cameFrom: new Map<string, number>(),
    leftTo: new Map<string, number>()
  }
}

function addRevenueSegmentWorkDay(
  segment: RevenueSegmentAccumulator,
  studioId: number,
  metricDate: string
) {
  segment.studios.add(studioId)
  segment.studioDays.add(`${studioId}:${metricDate}`)
}

function addRevenueSegmentExcludedDay(
  segment: RevenueSegmentAccumulator,
  studioId: number,
  metricDate: string
) {
  segment.studios.add(studioId)
  segment.excludedStudioDays.add(`${studioId}:${metricDate}`)
}

function incrementMovement(map: Map<string, number>, key: string) {
  map.set(key, (map.get(key) || 0) + 1)
}

function getMovementRows(map: Map<string, number>) {
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'ru'))
}

function addDaysToIsoDate(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00`)
  date.setDate(date.getDate() + days)
  return getLocalIsoDate(date)
}

function getDatesBetween(start: string, end: string): string[] {
  const dates: string[] = []
  if (end < start) return dates
  let cursor = start
  while (cursor <= end) {
    dates.push(cursor)
    cursor = addDaysToIsoDate(cursor, 1)
  }
  return dates
}

async function fetchAllRows<T>(
  createQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null, error: any }>,
  pageSize = 1000
): Promise<{ data: T[], error: any }> {
  const allRows: T[] = []
  let from = 0

  while (true) {
    const to = from + pageSize - 1
    const result = await createQuery(from, to)
    if (result.error) return { data: allRows, error: result.error }

    const rows = result.data || []
    allRows.push(...rows)

    if (rows.length < pageSize) break
    from += pageSize
  }

  return { data: allRows, error: null }
}

function MiniValue({
  label,
  value,
  icon,
  tone = 'neutral',
  size = 'normal'
}: {
  label: ReactNode
  value: ReactNode
  icon?: ReactNode
  tone?: 'neutral' | 'good' | 'bad' | 'accent'
  size?: 'normal' | 'sm'
}) {
  const toneClass = {
    neutral: 'text-slate-950',
    good: 'text-emerald-700',
    bad: 'text-red-600',
    accent: 'text-blue-700'
  }

  const valueSizeClass = size === 'sm' ? 'text-base' : 'text-xl'

  return (
    <div className="rounded-xl border border-slate-200 bg-card px-3 py-2.5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
        {icon}
        {label}
      </div>
      <div className={`mt-2 ${valueSizeClass} font-semibold leading-none ${toneClass[tone]}`}>
        {value}
      </div>
    </div>
  )
}

function formatNullableCurrency(value: number | null): string {
  return value === null ? '—' : formatFullCurrency(value)
}

function formatNullableCount(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('ru-RU')
}

function formatNullablePercent(value: number | null): string {
  return value === null ? '—' : formatPercent(value)
}

function formatDashboardDate(value: string | null): string | null {
  if (!value) return null
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function RoyaltyNetworkCard({
  data,
  isUnavailable,
}: {
  data: ManagementDashboardNetworkKpis | null
  isUnavailable: boolean
}) {
  const royaltyMonth = formatDashboardDate(data?.royaltyMonth ?? null)

  return (
    <Card className="border border-slate-200 bg-card shadow-sm">
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Роялти сети</h2>
            <p className="mt-1 text-sm text-slate-500">
              {isUnavailable
                ? 'Расчёт пока недоступен. Остальные показатели дашборда продолжают работать.'
                : 'Текущий календарный месяц: роялти и агентское, начисления, оплаты и ожидаемый остаток.'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>{royaltyMonth ?? 'Текущий календарный месяц'}{data?.royaltyPlanVersion ? ` · план v${data.royaltyPlanVersion}` : ''}</span>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-[repeat(3,minmax(0,1fr))_minmax(300px,1.35fr)]">
          <MiniValue label="План к начислению" value={formatNullableCurrency(data?.royaltyPlanRub ?? null)} icon={<FileText className="h-4 w-4 text-slate-500" />} />
          <MiniValue label="Выставлено" value={formatNullableCurrency(data?.royaltyChargedRub ?? null)} icon={<WalletCards className="h-4 w-4 text-amber-700" />} />
          <MiniValue label="Оплачено деньгами" value={formatNullableCurrency(data?.royaltyPaidRub ?? null)} tone="good" icon={<CheckCircle className="h-4 w-4 text-emerald-700" />} />
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3">
            <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-[0.08em] text-blue-700">
              <span>Прогноз поступлений</span>
              <span className="relative inline-flex items-center group cursor-help normal-case tracking-normal">
                <button
                  type="button"
                  className="rounded p-0.5 text-blue-600 transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label="Как считается прогноз поступлений"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                <span className="pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-80 whitespace-normal rounded-lg bg-slate-950 px-3 py-2.5 text-left text-xs font-normal leading-snug text-white shadow-xl group-hover:block group-focus-within:block">
                  Прогноз итоговых поступлений = большая из сумм «План к начислению» и «Выставлено» × средняя денежная собираемость за два предыдущих закрытых месяца. Он не может быть ниже уже оплаченной суммы. «Ещё ожидаем» = прогноз минус уже оплачено.
                </span>
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div>
                <div className="text-xl font-semibold leading-none text-blue-950">{formatNullableCurrency(data?.royaltyExpectedTotalRub ?? null)}</div>
                <div className="mt-1 text-xs text-blue-700">итог за месяц</div>
              </div>
              <div className="text-right">
                <div className="text-base font-semibold leading-none text-blue-900">{formatNullableCurrency(data?.royaltyExpectedRemainingRub ?? null)}</div>
                <div className="mt-1 text-xs text-blue-700">ещё ожидаем</div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>Собираемость за 2 закрытых месяца: <strong className="font-semibold text-slate-800">{formatNullablePercent(data?.royaltyCollectionRatePercent ?? null)}</strong></span>
          <span>Долг текущего месяца: <strong className="font-semibold text-slate-800">{formatNullableCurrency(data?.royaltyDebtRub ?? null)}</strong></span>
        </div>
      </div>
    </Card>
  )
}

function NetworkFixedCdDashboardCard({
  snapshot
}: {
  snapshot: CurrentWeekNetworkFixedCdSnapshot
}) {
  const snapshotDate = new Date(`${snapshot.snapshot_date}T00:00:00`).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit'
  })

  return (
    <div className="overflow-hidden rounded-xl border border-amber-300/80 bg-gradient-to-br from-amber-50 via-card to-amber-50/60 p-4">
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-400 text-amber-950">
              <WalletCards className="h-4 w-4" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-amber-800">
              Цифра дня сети
            </div>
          </div>

          <div className="shrink-0 rounded-md bg-amber-100 px-2 py-1 text-[11px] font-medium leading-none text-amber-800">
            {snapshotDate}
          </div>
        </div>

        <div className="mt-auto grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-t border-amber-200/80 pt-4">
          <div>
            <div className="text-3xl font-bold leading-none tracking-tight text-slate-950">
              {formatCurrency(snapshot.fixed_cd_rub)}
            </div>
            <div className="mt-1.5 text-sm text-slate-600">
              на студию в день
            </div>
          </div>

          <div className="border-l border-amber-200/80 pl-4 text-right">
            <div className="text-xs text-slate-500">Осталось дней</div>
            <div className="mt-1 text-sm font-semibold leading-none text-slate-950">
              {snapshot.days_remaining} {snapshot.days_remaining === 1 ? 'день' : 'дн.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PlanFactBlock({
  title,
  planLabel,
  factLabel,
  plan,
  fact,
  factToDate,
  planToDatePercent,
  planToDateRatio,
  yesterdayRevenue,
  todayRevenue,
  daysRemaining,
  activeStudiosForDay,
  planLabelAction,
  tone,
  rangeState
}: {
  title: string
  planLabel: string
  factLabel: string
  plan: number
  fact: number
  factToDate: number
  planToDatePercent: number
  planToDateRatio: number
  yesterdayRevenue: number
  todayRevenue: number
  daysRemaining: number
  activeStudiosForDay: number
  planLabelAction?: ReactNode
  tone: 'gold' | 'gray'
  rangeState: RangeState
}) {
  const progressToday = plan > 0 ? Math.round((fact / plan) * 1000) / 10 : 0
  const progressToDate = plan > 0 ? Math.round((factToDate / plan) * 1000) / 10 : 0
  const expectedToDate = Math.round(plan * planToDateRatio)
  const differenceToDate = factToDate - expectedToDate
  const remaining = Math.max(plan - fact, 0)
  const networkPerDay = daysRemaining > 0 ? Math.round(remaining / daysRemaining) : 0
  const perStudioPerDay = activeStudiosForDay > 0 ? Math.round(networkPerDay / activeStudiosForDay) : 0

  const clampedProgressToDate = Math.min(Math.max(progressToDate, 0), 100)
  const clampedPlanToDate = Math.min(Math.max(planToDatePercent, 0), 100)

  const isBehind = differenceToDate < 0
  const showDailyDone = rangeState === 'current'
  const showRemaining = rangeState !== 'past'

  const colors = {
    gold: {
      wrapper: 'border-[#E4D29A] bg-card',
      bar: 'bg-[#E9BE3A]',
      percent: isBehind ? 'text-red-600' : 'text-emerald-700',
      label: 'text-[#9A7621]'
    },
    gray: {
      wrapper: 'border-slate-300 bg-card',
      bar: 'bg-slate-500',
      percent: isBehind ? 'text-red-600' : 'text-emerald-700',
      label: 'text-slate-600'
    }
  }[tone]

  const formatOrDash = (value: number, show: boolean) => (show ? formatFullCurrency(value) : '—')

  return (
    <Card className={`min-w-0 border-2 shadow-sm ${colors.wrapper}`}>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
          </div>

          <div className="text-right">
            <div className={`text-2xl font-bold leading-none ${colors.percent}`}>
              {formatPercent(progressToday)}
            </div>
          </div>
        </div>

        <div className="grid gap-1.5 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-card p-3">
            <div className={`text-xs font-medium uppercase tracking-[0.08em] ${colors.label}`}>
              {factLabel}
            </div>
            <div className="mt-2 text-xl font-semibold leading-none text-slate-950">
              {formatFullCurrency(fact)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-card p-3">
            <div className="flex items-center justify-between gap-2">
              <div className={`text-xs font-medium uppercase tracking-[0.08em] ${colors.label}`}>
                {planLabel}
              </div>
              {planLabelAction}
            </div>
            <div className="mt-2 text-xl font-semibold leading-none text-slate-950">
              {formatFullCurrency(plan)}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative h-5">
            <div className="absolute inset-x-0 top-1 h-3 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
              <div
                className={`h-full rounded-full ${colors.bar}`}
                style={{ width: `${clampedProgressToDate}%` }}
              />
            </div>

            <div
              className="absolute top-0 z-10 h-5 w-[2px] rounded-full bg-slate-600 shadow-sm"
              style={{
                left: `${clampedPlanToDate}%`,
                transform: 'translateX(-50%)'
              }}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>{rangeState === 'current' ? 'Факт на вчера' : 'Факт периода'}: {formatPercent(progressToDate)}</span>
            <span>{rangeState === 'current' ? 'План на вчера' : 'План периода'}: {formatPercent(planToDatePercent)}</span>
          </div>
        </div>

        <div className="grid gap-1.5 sm:grid-cols-3">
          <MiniValue label={rangeState === 'current' ? 'План на вчера' : 'План периода'} value={formatFullCurrency(expectedToDate)} size="sm" />
          <MiniValue label={rangeState === 'current' ? 'Факт на вчера' : 'Факт периода'} value={formatFullCurrency(factToDate)} size="sm" />
          <MiniValue
            label="Опережение / отставание"
            value={formatSignedCurrency(differenceToDate)}
            tone={isBehind ? 'bad' : 'good'}
            size="sm"
          />
        </div>

        <div className="grid gap-1.5 sm:grid-cols-2 2xl:grid-cols-5">
          <MiniValue
            label="Осталось"
            value={formatOrDash(remaining, showRemaining)}
            size="sm"
          />
          <MiniValue
            label="Нужно в день"
            value={formatOrDash(networkPerDay, showRemaining)}
            size="sm"
          />
          <MiniValue
            label="В день на студию"
            value={formatOrDash(perStudioPerDay, showRemaining)}
            size="sm"
          />
          <MiniValue
            label="Сделано вчера"
            value={formatOrDash(yesterdayRevenue, showDailyDone)}
            size="sm"
          />
          <MiniValue
            label="Сделано сегодня"
            value={formatOrDash(todayRevenue, showDailyDone)}
            size="sm"
          />
        </div>
      </div>
    </Card>
  )
}

export function ManagementCompanyDashboard() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isManagementCompany, studios } = useStudioStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const periods = useStudioPeriods()
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange
  } = useAnalyticsTimeFilter(periods.currentPeriod)

  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<ManagementDashboardData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [managementAccessLevel, setManagementAccessLevel] = useState<'standard' | 'advanced' | null>(null)
  const [isUkPlanModalOpen, setIsUkPlanModalOpen] = useState(false)
  const [ukPlanWeeks, setUkPlanWeeks] = useState<UkWeeklyPlanRow[]>([])
  const [networkFixedCd, setNetworkFixedCd] = useState<CurrentWeekNetworkFixedCdSnapshot | null>(null)
  const [networkKpis, setNetworkKpis] = useState<ManagementDashboardNetworkKpis | null>(null)
  const [networkKpisUnavailable, setNetworkKpisUnavailable] = useState(false)

  useEffect(() => {
    if (!isManagementCompany()) {
      console.warn('❌ Access denied: User is not management company')
      navigate('/')
      return
    }
  }, [isManagementCompany, navigate])

  useEffect(() => {
    let cancelled = false

    if (!user?.id || !isManagementCompany()) {
      setManagementAccessLevel(null)
      return
    }

    getAccessLevel().then((level) => {
      if (!cancelled) {
        setManagementAccessLevel(level)
      }
    })

    return () => {
      cancelled = true
    }
  }, [user?.id, isManagementCompany, getAccessLevel])

  useEffect(() => {
    if (periods.loading) return

    if (periods.error) {
      setError('Не удалось загрузить периоды')
      setLoading(false)
      return
    }

    if (user?.id && periods.currentPeriod && selectedRange && isManagementCompany()) {
      loadDashboardData()
      return
    }
    setLoading(false)
  }, [user?.id, periods.loading, periods.error, periods.currentPeriod?.period_id, selectedRange?.start, selectedRange?.end, isManagementCompany, studios.length])

  useEffect(() => {
    let isActive = true

    const loadNetworkFixedCd = async () => {
      if (!periods.currentPeriod || !selectedRange || selectedRange.mode !== 'week' || !selectedRange.weekNumber) {
        setNetworkFixedCd(null)
        return
      }

      const today = getLocalIsoDate()
      const isCurrentWeek = selectedRange.start <= today && selectedRange.end >= today
      if (!isCurrentWeek) {
        setNetworkFixedCd(null)
        return
      }

      try {
        const snapshot = await getCurrentWeekNetworkFixedCd(periods.currentPeriod.period_id, selectedRange.weekNumber)
        if (isActive) {
          setNetworkFixedCd(snapshot)
        }
      } catch (err) {
        if (isActive) {
          console.error('ManagementCompanyDashboard network fixed cd:', err)
          setNetworkFixedCd(null)
        }
      }
    }

    loadNetworkFixedCd()

    return () => {
      isActive = false
    }
  }, [periods.currentPeriod, selectedRange])

  useEffect(() => {
    let isActive = true
    const currentPeriod = periods.currentPeriod

    if (!user?.id || !isManagementCompany() || !currentPeriod) {
      setNetworkKpis(null)
      setNetworkKpisUnavailable(false)
      return () => {
        isActive = false
      }
    }

    setNetworkKpis(null)
    setNetworkKpisUnavailable(false)
    getManagementDashboardNetworkKpis(
      currentPeriod.period_id,
      selectedRange?.mode === 'week' ? selectedRange.weekNumber : null,
    ).then((data) => {
      if (isActive) setNetworkKpis(data)
    }).catch((loadError) => {
      console.warn('ManagementCompanyDashboard network KPIs:', loadError)
      if (isActive) setNetworkKpisUnavailable(true)
    })

    return () => {
      isActive = false
    }
  }, [user?.id, isManagementCompany, periods.currentPeriod?.period_id, selectedRange?.mode, selectedRange?.weekNumber])

  const loadDashboardData = async () => {
    const currentPeriod = periods.currentPeriod
    const range = selectedRange
    if (!user?.id || !currentPeriod || !range) return

    try {
      setLoading(true)
      setError(null)
      console.log('🔄 Loading management company dashboard data for period:', currentPeriod.period_id)

      const supabase = getSupabase()

      const [analyticsResult, problemsResult, managementStudiosResult] = await Promise.all([
        withAuthRecovery(() =>
          (supabase as any).rpc('get_studios_data_analytics_v2', {
            p_user_id: user.id,
            p_period_id: currentPeriod.period_id
          })
        ),
        withAuthRecovery(() =>
          (supabase as any).rpc('get_problem_studios_v2', {
            p_user_id: user.id,
            threshold_percentage: 70.0,
            p_period_id: currentPeriod.period_id
          })
        ),
        withAuthRecovery(() =>
          (supabase as any).rpc('get_management_company_studios_v2', {
            p_user_id: user.id
          })
        )
      ])

      if (analyticsResult.error) {
        console.error('❌ Analytics error details:', analyticsResult.error)
        throw new Error(`Analytics error: ${analyticsResult.error.message}`)
      }

      if (problemsResult.error) {
        console.error('❌ Problems error details:', problemsResult.error)
        throw new Error(`Problems error: ${problemsResult.error.message}`)
      }

      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = getLocalIsoDate(yesterday)

      const rawManagementStudios = (managementStudiosResult.data || []) as ManagementCompanyStudioRow[]
      const dashboardStudios = rawManagementStudios.length > 0
        ? rawManagementStudios.filter((studio) => {
            const studioId = Number(studio.studio_internal_id)
            if (!Number.isFinite(studioId) || studioId <= 0) return false

            const partnerNumberRaw = studio.partner_number
            const partnerNumber = partnerNumberRaw === null || partnerNumberRaw === undefined || partnerNumberRaw === ''
              ? null
              : Number(partnerNumberRaw)

            return partnerNumber !== MANAGEMENT_BRANCH_PARTNER_NUMBER
          })
        : studios.filter((studio) =>
            Number.isFinite(studio.studio_internal_id)
            && studio.partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER
          )
      if (managementStudiosResult.error) {
        console.warn('⚠️ ManagementCompanyDashboard: failed to load full management studio list, fallback to store', managementStudiosResult.error)
      }
      const dashboardStudioIds = Array.from(new Set(
        dashboardStudios
          .map((studio) => Number(studio.studio_internal_id))
          .filter((value) => Number.isFinite(value) && value > 0)
      ))
      const dashboardStudioNumbers = dashboardStudios
        .map((studio) => Number(studio.studio_number))
        .filter((value) => Number.isFinite(value) && value > 0)
      const dashboardCityIds = Array.from(new Set(
        dashboardStudios
          .map((studio) => Number(studio.city_id))
          .filter((value) => Number.isFinite(value) && value > 0)
      ))
      const dashboardStudioIdSet = new Set(dashboardStudioIds)
      const analytics: StudioAnalytics[] = (analyticsResult.data || []).filter((row: StudioAnalytics) =>
        dashboardStudioIdSet.has(Number(row.studio_internal_id))
      )
      const problems: StudioIssue[] = (problemsResult.data || []).filter((row: StudioIssue) =>
        dashboardStudioIdSet.has(Number(row.studio_internal_id))
      )
      const todayIso = getLocalIsoDate(new Date())

      const [
        periodDailyRowsResult,
        weeklyPlanRows,
        weeklyPlanDetailRowsResult,
        selectedRangeRevenueRows,
        todayRevenueResult,
        yesterdayRevenueResult,
        clientsRows,
        crmRows,
        metricWeeksRows,
        ukPlanRowsResult,
        subscriptionSettingsRows,
        subscriptionHistoryRows,
        statusHistoryRows,
        studiosRows,
        citiesRows
      ] = await Promise.all([
        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) =>
              supabase
                .from('studio_daily_totals')
                .select('studio_internal_id, metric_date, gross_revenue_confirmed, last_saved_by_user_id')
                .gte('metric_date', range.start)
                .lte('metric_date', range.end)
                .in('studio_internal_id', dashboardStudioIds)
                .order('metric_date', { ascending: true })
                .order('studio_internal_id', { ascending: true })
                .range(from, to)
            ),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) => {
              let query = supabase
                .from('studio_weekly_plan_vs_fact_rub')
                .select('studio_internal_id, week_id, week_number_in_period, week_start, week_end, week_len_days, plan_fx_rate_to_rub, weekly_gross_revenue_plan_rub, weekly_gross_revenue_fact')
                .eq('period_id', currentPeriod.period_id)
                .in('studio_internal_id', dashboardStudioIds)
                .order('week_id', { ascending: true })
                .order('studio_internal_id', { ascending: true })

              if (range.mode === 'week' && range.weekNumber) {
                query = query.eq('week_number_in_period', range.weekNumber)
              }

              return query.range(from, to)
            }),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) => {
              let query = supabase
                .from('studio_weekly_plan_vs_fact')
                .select(`
                  studio_internal_id,
                  week_id,
                  week_number_in_period,
                  week_start,
                  week_end,
                  week_len_days,
                  weekly_gross_revenue_plan,
                  weekly_marketing_budget_plan,
                  weekly_leads_plan,
                  weekly_appointments_plan,
                  weekly_new_visitors_plan,
                  weekly_new_sales_plan,
                  weekly_new_sales_amount_plan,
                  weekly_new_subscriptions_count_plan,
                  weekly_new_subscriptions_amount_plan,
                  weekly_membership_fees_new_plan
                `)
                .eq('period_id', currentPeriod.period_id)
                .in('studio_internal_id', dashboardStudioIds)
                .order('week_id', { ascending: true })
                .order('studio_internal_id', { ascending: true })

              if (range.mode === 'week' && range.weekNumber) {
                query = query.eq('week_number_in_period', range.weekNumber)
              }

              return query.range(from, to)
            }),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) =>
              supabase
                .from('studio_daily_totals_with_rub')
                .select(`
                  studio_internal_id,
                  metric_date,
                  country,
                  currency_code,
                  gross_revenue_rub,
                  marketing_expenses_amount_rub,
                  leads_count,
                  appointments_count,
                  new_visitors_count,
                  new_sales_count,
                  new_subscriptions_count,
                  new_sales_amount_rub,
                  new_subscriptions_amount_rub,
                  membership_fees_new_amount_rub
                `)
                .gte('metric_date', range.start)
                .lte('metric_date', range.end)
                .in('studio_internal_id', dashboardStudioIds)
                .order('metric_date', { ascending: true })
                .order('studio_internal_id', { ascending: true })
                .range(from, to)
            ),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from('studio_daily_totals_with_rub')
              .select('studio_internal_id, metric_date, gross_revenue_rub')
              .eq('metric_date', todayIso)
              .in('studio_internal_id', dashboardStudioIds),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from('studio_daily_totals_with_rub')
              .select('studio_internal_id, metric_date, gross_revenue_rub')
              .eq('metric_date', yesterdayStr)
              .in('studio_internal_id', dashboardStudioIds),

        // Без .in(dashboardStudioIds): «Клиенты»/«Подписчики» — показатель по всей сети,
        // а не только по студиям, доступным конкретному пользователю УК (у не-advanced
        // доступа get_my_studios не отдаёт CLOSED/CANCELLED студии, хотя их клиенты реальны —
        // см. CLAUDE.md/ETL-пайплайны). RLS на studio_daily_totals сейчас passthrough,
        // так что сужение запроса тут было чисто клиентским, а не защитой доступа.
        fetchAllRows((from, to) =>
          supabase
            .from('studio_daily_totals')
            .select('studio_internal_id, metric_date, total_clients_count, active_subscribers_count')
            .gte('metric_date', range.start)
            .lte('metric_date', range.end)
            .order('metric_date', { ascending: true })
            .order('studio_internal_id', { ascending: true })
            .range(from, to)
        ),

        dashboardStudioNumbers.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) =>
              supabase
                .from('report_payments_with_rub')
                .select('studio_number, date, amount_rub')
                .gte('date', range.start)
                .lte('date', range.end)
                .in('studio_number', dashboardStudioNumbers)
                .order('date', { ascending: true })
                .order('id', { ascending: true })
                .range(from, to)
            ),

        supabase
          .from('metric_weeks')
          .select('week_id, period_id, week_number_in_period, week_start, week_end')
          .eq('period_id', currentPeriod.period_id)
          .order('week_number_in_period', { ascending: true }),

        supabase
          .from('uk_weekly_plan')
          .select('week_id, plan_revenue_rub, created_at, created_by_user_id, updated_at, updated_by_user_id'),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from('studio_subscription_settings')
              .select('studio_internal_id, subscription_type')
              .in('studio_internal_id', dashboardStudioIds),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) =>
              (supabase as any)
                .from('studio_subscription_settings_history')
                .select('studio_internal_id, old_subscription_type, new_subscription_type, changed_at')
                .in('studio_internal_id', dashboardStudioIds)
                .order('changed_at', { ascending: true })
                .order('history_id', { ascending: true })
                .range(from, to)
            ),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : fetchAllRows((from, to) =>
              (supabase as any)
                .from('studio_status_history')
                .select('studio_internal_id, old_status_code, new_status_code, changed_at')
                .in('studio_internal_id', dashboardStudioIds)
                .order('changed_at', { ascending: true })
                .order('history_id', { ascending: true })
                .range(from, to)
            ),

        dashboardStudioIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from('studios')
              .select('studio_internal_id, status_code')
              .in('studio_internal_id', dashboardStudioIds),

        dashboardCityIds.length === 0
          ? Promise.resolve({ data: [], error: null })
          : supabase
              .from('cities')
              .select('city_id, country, city_land_group')
              .in('city_id', dashboardCityIds)
      ])

      ;[
        periodDailyRowsResult,
        weeklyPlanRows,
        weeklyPlanDetailRowsResult,
        selectedRangeRevenueRows,
        todayRevenueResult,
        yesterdayRevenueResult,
        clientsRows,
        crmRows,
        metricWeeksRows,
        ukPlanRowsResult,
        subscriptionSettingsRows,
        subscriptionHistoryRows,
        statusHistoryRows,
        studiosRows,
        citiesRows
      ].forEach((result: any) => {
        if (result?.error) throw result.error
      })

      const planRows = (weeklyPlanRows.data || []) as Array<{
        studio_internal_id: number
        week_id: number
        week_start: string
        week_end: string
        week_len_days: number | null
        plan_fx_rate_to_rub: number | null
        weekly_gross_revenue_plan_rub: number | null
        weekly_gross_revenue_fact: number | null
      }>
      const weeklyPlanDetailRows = (weeklyPlanDetailRowsResult.data || []) as WeeklyPlanDetailRow[]
      const metricWeeks = (metricWeeksRows.data || []) as Array<{
        week_id: number
        period_id: number
        week_number_in_period: number
        week_start: string
        week_end: string
      }>
      const ukPlanEntries = (ukPlanRowsResult.data || []) as Array<{
        week_id: number
        plan_revenue_rub: number | null
        created_at: string | null
        created_by_user_id: string | null
        updated_at: string | null
        updated_by_user_id: string | null
      }>
      const ukPlanEntryByWeek = new Map<number, typeof ukPlanEntries[number]>()
      ukPlanEntries.forEach((row) => {
        ukPlanEntryByWeek.set(row.week_id, row)
      })
      const ukPlanRows: UkWeeklyPlanRow[] = metricWeeks.map((week) => {
        const plan = ukPlanEntryByWeek.get(week.week_id)
        const weekLenDays = Math.max(
          1,
          getInclusiveDays(week.week_start, week.week_end)
        )

        return {
          week_id: week.week_id,
          period_id: week.period_id,
          week_number_in_period: week.week_number_in_period,
          week_start: week.week_start,
          week_end: week.week_end,
          week_len_days: weekLenDays,
          plan_revenue_rub: plan?.plan_revenue_rub ?? null,
          created_at: plan?.created_at ?? null,
          created_by_user_id: plan?.created_by_user_id ?? null,
          updated_at: plan?.updated_at ?? null,
          updated_by_user_id: plan?.updated_by_user_id ?? null
        }
      })
      setUkPlanWeeks(ukPlanRows)
      const periodDailyRows = (periodDailyRowsResult.data || []) as Array<{
        studio_internal_id: number
        metric_date: string
        gross_revenue_confirmed: boolean | null
        last_saved_by_user_id: number | string | null
      }>

      const totalsByStudio = new Map<number, number>()
      planRows.forEach((row) => {
        totalsByStudio.set(
          row.studio_internal_id,
          (totalsByStudio.get(row.studio_internal_id) || 0) + toFiniteNumber(row.weekly_gross_revenue_plan_rub)
        )
      })

      const partnerPlan = planRows.reduce((sum, row) => sum + toFiniteNumber(row.weekly_gross_revenue_plan_rub), 0)
      const selectedManagementPlanRows = range.mode === 'week' && range.weekNumber
        ? ukPlanRows.filter((row) => row.week_number_in_period === range.weekNumber)
        : ukPlanRows
      const managementPlan = selectedManagementPlanRows.reduce(
        (sum, row) => sum + toFiniteNumber(row.plan_revenue_rub),
        0
      )
      const rangeRevenueData = (selectedRangeRevenueRows.data || []) as Array<{
        studio_internal_id: number
        metric_date: string
        country: string | null
        currency_code: string | null
        gross_revenue_rub: number | null
        marketing_expenses_amount_rub: number | null
        leads_count: number | null
        appointments_count: number | null
        new_visitors_count: number | null
        new_sales_count: number | null
        new_subscriptions_count: number | null
        new_sales_amount_rub: number | null
        new_subscriptions_amount_rub: number | null
        membership_fees_new_amount_rub: number | null
      }>
      const crmData = (crmRows.data || []) as Array<{
        studio_number: number | string | null
        date: string | null
        amount_rub: number | string | null
      }>
      const studioIdByStudioNumber = new Map<number, number>()
      dashboardStudios.forEach((studio) => {
        const studioNumber = Number(studio.studio_number)
        if (Number.isFinite(studioNumber)) {
          studioIdByStudioNumber.set(studioNumber, studio.studio_internal_id)
        }
      })
      const listokRevenueByStudioDate = new Map<string, {
        studio_internal_id: number
        metric_date: string
        gross_revenue_rub: number
      }>()
      crmData.forEach((row) => {
        if (!row.date) return
        const studioNumber = Number(row.studio_number)
        const studioId = studioIdByStudioNumber.get(studioNumber)
        if (!studioId) return

        const key = `${studioId}:${row.date}`
        const existingRow = listokRevenueByStudioDate.get(key)
        const revenue = toFiniteNumber(row.amount_rub)
        if (existingRow) {
          existingRow.gross_revenue_rub += revenue
          return
        }

        listokRevenueByStudioDate.set(key, {
          studio_internal_id: studioId,
          metric_date: row.date,
          gross_revenue_rub: revenue
        })
      })
      const listokRevenueData = Array.from(listokRevenueByStudioDate.values())
      const cityGeographyById = new Map<number, CityGeographyRow>()
      ;((citiesRows.data || []) as CityGeographyRow[]).forEach((row) => {
        cityGeographyById.set(row.city_id, row)
      })

      const subscriptionTypeByStudio = new Map<number, StudioSubscriptionKind>()
      ;((subscriptionSettingsRows.data || []) as Array<{
        studio_internal_id: number
        subscription_type: StudioSubscriptionKind | null
      }>).forEach((row) => {
        if (!dashboardStudioIdSet.has(Number(row.studio_internal_id))) return
        subscriptionTypeByStudio.set(row.studio_internal_id, row.subscription_type || 'REGULAR')
      })

      const subscriptionHistoryByStudio = new Map<number, SubscriptionHistoryRow[]>()
      ;((subscriptionHistoryRows.data || []) as SubscriptionHistoryRow[]).forEach((row) => {
        if (!row.studio_internal_id || !row.changed_at) return
        const existingRows = subscriptionHistoryByStudio.get(row.studio_internal_id) || []
        existingRows.push(row)
        subscriptionHistoryByStudio.set(row.studio_internal_id, existingRows)
      })
      subscriptionHistoryByStudio.forEach((rows) => {
        rows.sort((a, b) => String(a.changed_at || '').localeCompare(String(b.changed_at || '')))
      })

      const currentStatusByStudio = new Map<number, StudioStatusCode>()
      ;((studiosRows.data || []) as Array<{ studio_internal_id: number, status_code: StudioStatusCode | null }>).forEach((row) => {
        if (!row.studio_internal_id) return
        currentStatusByStudio.set(row.studio_internal_id, row.status_code || 'CLOSED')
      })

      const statusHistoryByStudio = new Map<number, StudioStatusHistoryRow[]>()
      ;((statusHistoryRows.data || []) as StudioStatusHistoryRow[]).forEach((row) => {
        if (!row.studio_internal_id || !row.changed_at) return
        const existingRows = statusHistoryByStudio.get(row.studio_internal_id) || []
        existingRows.push(row)
        statusHistoryByStudio.set(row.studio_internal_id, existingRows)
      })
      statusHistoryByStudio.forEach((rows) => {
        rows.sort((a, b) => String(a.changed_at || '').localeCompare(String(b.changed_at || '')))
      })

      const getEffectiveSubscriptionType = (studioId: number, metricDate: string) => {
        const configuredType = subscriptionTypeByStudio.get(studioId) || 'REGULAR'
        const historyRows = subscriptionHistoryByStudio.get(studioId) || []

        const nearestFutureChange = historyRows.find((row) => row.changed_at && String(row.changed_at).slice(0, 10) > metricDate)
        if (nearestFutureChange?.old_subscription_type) {
          return nearestFutureChange.old_subscription_type
        }

        return configuredType
      }

      const getEffectiveStudioStatus = (studioId: number, metricDate: string) => {
        const currentStatus = currentStatusByStudio.get(studioId) || 'CLOSED'
        const historyRows = statusHistoryByStudio.get(studioId) || []
        const latestPastChange = [...historyRows]
          .reverse()
          .find((row) => row.changed_at && String(row.changed_at).slice(0, 10) <= metricDate)

        if (latestPastChange?.new_status_code) {
          return latestPastChange.new_status_code
        }

        const nearestFutureChange = historyRows.find((row) => row.changed_at && String(row.changed_at).slice(0, 10) > metricDate)
        if (nearestFutureChange?.old_status_code) {
          return nearestFutureChange.old_status_code
        }

        return currentStatus
      }

      const appFact = rangeRevenueData.reduce(
        (sum, row) => sum + toFiniteNumber(row.gross_revenue_rub),
        0
      )

      const segmentAccumulators = [
        createRevenueSegment('all', 'Вся сеть', 'Все open-студио-дни в выбранном диапазоне'),
        createRevenueSegment('regular', 'Абонементы (РФ)', 'Студии РФ, работающие по абонементной модели'),
        createRevenueSegment('subscription', 'Подписки', 'Студии, работающие по подписочной модели'),
        createRevenueSegment('mixed', 'Смешанная модель', 'Студии, где есть и абонементы, и подписки'),
        createRevenueSegment('foreign', 'Иностранные студии', 'Иностранные студии на абонементной модели, в рублевом эквиваленте')
      ]
      const segmentByKey = new Map(segmentAccumulators.map((segment) => [segment.key, segment]))
      const cityIdByStudio = new Map<number, number>()
      const geographySegmentAccumulators = [
        {
          key: 'russia' as const,
          label: 'РФ',
          description: 'Студии в РФ',
          studioIds: new Set<number>(),
          manualStudios: new Set<number>(),
          marketingStudios: new Set<number>(),
          planMarketingStudios: new Set<number>(),
          regularStudios: new Set<number>(),
          subscriptionStudios: new Set<number>(),
          planFilledStudios: new Set<number>(),
          planRegularStudios: new Set<number>(),
          planSubscriptionStudios: new Set<number>(),
          totals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          },
          planTotals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          }
        },
        {
          key: 'near_abroad' as const,
          label: 'Ближнее зарубежье',
          description: 'Студии из земель ближнего зарубежья',
          studioIds: new Set<number>(),
          manualStudios: new Set<number>(),
          marketingStudios: new Set<number>(),
          planMarketingStudios: new Set<number>(),
          regularStudios: new Set<number>(),
          subscriptionStudios: new Set<number>(),
          planFilledStudios: new Set<number>(),
          planRegularStudios: new Set<number>(),
          planSubscriptionStudios: new Set<number>(),
          totals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          },
          planTotals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          }
        },
        {
          key: 'other_foreign' as const,
          label: 'Прочие иностранцы',
          description: 'Остальные иностранные студии',
          studioIds: new Set<number>(),
          manualStudios: new Set<number>(),
          marketingStudios: new Set<number>(),
          planMarketingStudios: new Set<number>(),
          regularStudios: new Set<number>(),
          subscriptionStudios: new Set<number>(),
          planFilledStudios: new Set<number>(),
          planRegularStudios: new Set<number>(),
          planSubscriptionStudios: new Set<number>(),
          totals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          },
          planTotals: {
            marketingExpenses: 0,
            marketingLeads: 0,
            leads: 0,
            appointments: 0,
            visitors: 0,
            regularVisitors: 0,
            subscriptionVisitors: 0,
            regularSales: 0,
            subscriptionSales: 0,
            regularRevenue: 0,
            subscriptionRevenue: 0
          }
        }
      ]
      const geographySegmentByKey = new Map(
        geographySegmentAccumulators.map((segment) => [segment.key, segment])
      )

      dashboardStudios.forEach((studio) => {
        cityIdByStudio.set(studio.studio_internal_id, Number(studio.city_id))
      })

      const elapsedRangeEnd = getElapsedRangeEnd(range.start, range.end, todayIso)
      const workDates = elapsedRangeEnd ? getDatesBetween(range.start, elapsedRangeEnd) : []
      const allRangeDates = getDatesBetween(range.start, range.end)
      const remainingRangeStart = todayIso < range.start ? range.start : todayIso
      const remainingWorkDates = todayIso > range.end ? [] : getDatesBetween(remainingRangeStart, range.end)
      const rangeWorkingStudios = new Set<number>()
      const elapsedWorkingStudios = new Set<number>()
      const remainingWorkingStudios = new Set<number>()

      dashboardStudioIds.forEach((studioId) => {
        allRangeDates.forEach((metricDate) => {
          const status = getEffectiveStudioStatus(studioId, metricDate)
          if (!OPEN_STUDIO_STATUSES.has(status)) return
          rangeWorkingStudios.add(studioId)
          geographySegmentByKey.get(getGeographySegmentKey(studioId))?.studioIds.add(studioId)
        })

        workDates.forEach((metricDate) => {
          const status = getEffectiveStudioStatus(studioId, metricDate)
          const segmentKey = getStudioRevenueSegmentKey(studioId, metricDate)

          if (!OPEN_STUDIO_STATUSES.has(status)) {
            addRevenueSegmentExcludedDay(segmentByKey.get(segmentKey)!, studioId, metricDate)
            return
          }

          elapsedWorkingStudios.add(studioId)
          addRevenueSegmentWorkDay(segmentByKey.get('all')!, studioId, metricDate)
          addRevenueSegmentWorkDay(segmentByKey.get(segmentKey)!, studioId, metricDate)
        })

        remainingWorkDates.forEach((metricDate) => {
          const status = getEffectiveStudioStatus(studioId, metricDate)
          if (!OPEN_STUDIO_STATUSES.has(status)) return
          remainingWorkingStudios.add(studioId)
        })
      })

      const rangeState: RangeState =
        range.end < todayIso
          ? 'past'
          : range.start > todayIso
            ? 'future'
            : 'current'

      const comparisonDate =
        rangeState === 'current'
          ? (yesterdayStr < range.start ? null : yesterdayStr > range.end ? range.end : yesterdayStr)
          : rangeState === 'past'
            ? range.end
            : null

      const summaryDate = rangeState === 'future'
        ? range.start
        : (elapsedRangeEnd || range.end)
      const summaryWorkingStudios = new Set<number>()
      dashboardStudioIds.forEach((studioId) => {
        const status = getEffectiveStudioStatus(studioId, summaryDate)
        if (OPEN_STUDIO_STATUSES.has(status)) {
          summaryWorkingStudios.add(studioId)
        }
      })

      const hasManualSave = (row: { last_saved_by_user_id: number | string | null }) =>
        row.last_saved_by_user_id !== null &&
        row.last_saved_by_user_id !== undefined &&
        String(row.last_saved_by_user_id).trim() !== ''

      const rowsByStudioAndDate = new Map<string, {
        studio_internal_id: number
        metric_date: string
        gross_revenue_confirmed: boolean | null
        last_saved_by_user_id: number | string | null
      }>()
      periodDailyRows.forEach((row) => {
        rowsByStudioAndDate.set(`${row.studio_internal_id}:${row.metric_date}`, row)
      })

      function getGeographySegmentKey(studioId: number): GeographySegmentKey {
        const cityId = cityIdByStudio.get(studioId)
        const geography = cityId ? cityGeographyById.get(cityId) : null

        if (geography?.country === 'РФ') {
          return 'russia'
        }

        if (geography?.city_land_group === NEAR_ABROAD_LAND_GROUP_CODE) {
          return 'near_abroad'
        }

        return 'other_foreign'
      }

      function getStudioRevenueSegmentKey(studioId: number, metricDate: string): RevenueSegmentKey {
        const subscriptionType = getEffectiveSubscriptionType(studioId, metricDate)
        const geographySegmentKey = getGeographySegmentKey(studioId)

        if (subscriptionType === 'SUBSCRIPTION') {
          return 'subscription'
        }

        if (subscriptionType === 'MIXED') {
          return 'mixed'
        }

        // Иностранные студии выделяем отдельным сегментом только для абонементной модели.
        if (geographySegmentKey !== 'russia') {
          return 'foreign'
        }

        return 'regular'
      }

      function getOpenStudioRevenueSegmentKey(studioId: number, metricDate: string): RevenueSegmentKey | null {
        const status = getEffectiveStudioStatus(studioId, metricDate)
        if (!OPEN_STUDIO_STATUSES.has(status)) return null
        return getStudioRevenueSegmentKey(studioId, metricDate)
      }

      const movementOutsideBaseLabel = 'без продаж/закрытые'
      dashboardStudioIds.forEach((studioId) => {
        const startSegmentKey = getOpenStudioRevenueSegmentKey(studioId, range.start)
        const endSegmentKey = getOpenStudioRevenueSegmentKey(studioId, range.end)

        if (endSegmentKey) {
          segmentByKey.get(endSegmentKey)?.endStudios.add(studioId)
        }

        if (startSegmentKey === endSegmentKey) return

        if (endSegmentKey) {
          const sourceLabel = startSegmentKey
            ? segmentByKey.get(startSegmentKey)?.label || startSegmentKey
            : movementOutsideBaseLabel
          incrementMovement(segmentByKey.get(endSegmentKey)!.cameFrom, sourceLabel)
        }

        if (startSegmentKey) {
          const targetLabel = endSegmentKey
            ? segmentByKey.get(endSegmentKey)?.label || endSegmentKey
            : movementOutsideBaseLabel
          incrementMovement(segmentByKey.get(startSegmentKey)!.leftTo, targetLabel)
        }
      })

      const fillCheckDate = comparisonDate
      const fillCheckWorkingStudios = new Set<number>()
      if (fillCheckDate) {
        dashboardStudioIds.forEach((studioId) => {
          const status = getEffectiveStudioStatus(studioId, fillCheckDate)
          if (OPEN_STUDIO_STATUSES.has(status)) {
            fillCheckWorkingStudios.add(studioId)
          }
        })
      }

      const studiosWithoutYesterdayData = fillCheckDate
        ? Array.from(fillCheckWorkingStudios).filter((studioId) => {
            const row = rowsByStudioAndDate.get(`${studioId}:${fillCheckDate}`)
            return !row || !hasManualSave(row)
          }).length
        : 0

      const elapsedWorkingDatesByStudio = new Map<number, string[]>()
      const completionRates: number[] = []
      const confirmationRates: number[] = []

      dashboardStudioIds.forEach((studioId) => {
        const workingDates = workDates.filter((metricDate) =>
          OPEN_STUDIO_STATUSES.has(getEffectiveStudioStatus(studioId, metricDate))
        )
        elapsedWorkingDatesByStudio.set(studioId, workingDates)

        if (workingDates.length === 0) return

        const filledDays = workingDates.filter((metricDate) => {
          const row = rowsByStudioAndDate.get(`${studioId}:${metricDate}`)
          return row ? hasManualSave(row) : false
        }).length

        const confirmedDays = workingDates.filter((metricDate) =>
          rowsByStudioAndDate.get(`${studioId}:${metricDate}`)?.gross_revenue_confirmed === true
        ).length

        completionRates.push((filledDays / workingDates.length) * 100)
        confirmationRates.push(filledDays > 0 ? (confirmedDays / filledDays) * 100 : 0)
      })

      const studiosWithoutConfirmations = dashboardStudioIds.filter((studioId) => {
        const workingDates = elapsedWorkingDatesByStudio.get(studioId) || []
        if (workingDates.length === 0) return false

        const unconfirmedDays = workingDates.filter((metricDate) => {
          const row = rowsByStudioAndDate.get(`${studioId}:${metricDate}`)
          return row && row.gross_revenue_confirmed !== true
        }).length

        return unconfirmedDays > 2
      }).length

      const studiosWithoutPlan = Array.from(rangeWorkingStudios).filter((studioId) => {
        const revenuePlan = totalsByStudio.get(studioId)
        return !revenuePlan || revenuePlan <= 0
      }).length

      const openStudiosCount = remainingWorkingStudios.size || summaryWorkingStudios.size || rangeWorkingStudios.size
      const marketingSpendByStudio = new Map<number, number>()
      const leadsByStudio = new Map<number, number>()
      const factByStudio = new Map<number, MarketingFactStudio>()
      const paceLeadsByStudio = new Map<number, number>()

      listokRevenueData.forEach((row) => {
        const revenue = toFiniteNumber(row.gross_revenue_rub)
        const segmentKey = getStudioRevenueSegmentKey(row.studio_internal_id, row.metric_date)

        if (!OPEN_STUDIO_STATUSES.has(getEffectiveStudioStatus(row.studio_internal_id, row.metric_date))) {
          segmentByKey.get(segmentKey)!.excludedRevenue += revenue
          return
        }

        segmentByKey.get('all')!.revenue += revenue
        segmentByKey.get(segmentKey)!.revenue += revenue
      })

      rangeRevenueData.forEach((row) => {
        const manualRow = rowsByStudioAndDate.get(`${row.studio_internal_id}:${row.metric_date}`)
        if (!manualRow || !hasManualSave(manualRow)) return
        if (!OPEN_STUDIO_STATUSES.has(getEffectiveStudioStatus(row.studio_internal_id, row.metric_date))) return
        if (!comparisonDate || row.metric_date > comparisonDate) return

        const geographySegment = geographySegmentByKey.get(getGeographySegmentKey(row.studio_internal_id))
        if (!geographySegment) return

        const subscriptionType = getEffectiveSubscriptionType(row.studio_internal_id, row.metric_date)
        const regularSalesCount = toFiniteNumber(row.new_sales_count)
        const subscriptionSalesCount = toFiniteNumber(row.new_subscriptions_count)
        const marketingExpenses = toFiniteNumber(row.marketing_expenses_amount_rub)
        const visitorsCount = toFiniteNumber(row.new_visitors_count)
        const leadsCount = toFiniteNumber(row.leads_count)

        paceLeadsByStudio.set(
          row.studio_internal_id,
          (paceLeadsByStudio.get(row.studio_internal_id) || 0) + leadsCount
        )

        const factStudio = factByStudio.get(row.studio_internal_id) || {
          marketingExpenses: 0,
          marketingLeads: 0,
          leads: 0,
          appointments: 0,
          visitors: 0,
          regularVisitors: 0,
          subscriptionVisitors: 0,
          regularSales: 0,
          subscriptionSales: 0,
          regularRevenue: 0,
          subscriptionRevenue: 0,
          hasRegularLine: false,
          hasSubscriptionLine: false
        }
        factStudio.marketingExpenses += marketingExpenses
        factStudio.marketingLeads += leadsCount
        factStudio.leads += leadsCount
        factStudio.appointments += toFiniteNumber(row.appointments_count)
        factStudio.visitors += visitorsCount

        geographySegment.manualStudios.add(row.studio_internal_id)
        geographySegment.totals.marketingExpenses += marketingExpenses
        geographySegment.totals.leads += leadsCount
        geographySegment.totals.appointments += toFiniteNumber(row.appointments_count)
        geographySegment.totals.visitors += visitorsCount

        if (subscriptionType === 'REGULAR' || subscriptionType === 'MIXED') {
          factStudio.hasRegularLine = true
          factStudio.regularVisitors += visitorsCount
          factStudio.regularSales += regularSalesCount
          factStudio.regularRevenue += toFiniteNumber(row.new_sales_amount_rub)
          geographySegment.regularStudios.add(row.studio_internal_id)
          geographySegment.totals.regularVisitors += visitorsCount
          geographySegment.totals.regularSales += regularSalesCount
          geographySegment.totals.regularRevenue += toFiniteNumber(row.new_sales_amount_rub)
        }

        if (subscriptionType === 'SUBSCRIPTION' || subscriptionType === 'MIXED') {
          factStudio.hasSubscriptionLine = true
          factStudio.subscriptionVisitors += visitorsCount
          factStudio.subscriptionSales += subscriptionSalesCount
          factStudio.subscriptionRevenue += toFiniteNumber(row.new_subscriptions_amount_rub)
          geographySegment.subscriptionStudios.add(row.studio_internal_id)
          geographySegment.totals.subscriptionVisitors += visitorsCount
          geographySegment.totals.subscriptionSales += subscriptionSalesCount
          geographySegment.totals.subscriptionRevenue +=
            toFiniteNumber(row.new_subscriptions_amount_rub)
        }

        marketingSpendByStudio.set(
          row.studio_internal_id,
          (marketingSpendByStudio.get(row.studio_internal_id) || 0) + marketingExpenses
        )
        leadsByStudio.set(
          row.studio_internal_id,
          (leadsByStudio.get(row.studio_internal_id) || 0) + leadsCount
        )
        factByStudio.set(row.studio_internal_id, factStudio)
      })

      marketingSpendByStudio.forEach((periodMarketingSpend, studioId) => {
        if (periodMarketingSpend <= 0) return
        const geographySegment = geographySegmentByKey.get(getGeographySegmentKey(studioId))
        if (!geographySegment) return
        geographySegment.marketingStudios.add(studioId)
        geographySegment.totals.marketingLeads += leadsByStudio.get(studioId) || 0
      })

      const planRubByStudioWeek = new Map<string, { fxRateToRub: number, grossRevenueRub: number }>()
      planRows.forEach((row) => {
        planRubByStudioWeek.set(`${row.studio_internal_id}:${row.week_id}`, {
          fxRateToRub: toFiniteNumber(row.plan_fx_rate_to_rub) || 1,
          grossRevenueRub: toFiniteNumber(row.weekly_gross_revenue_plan_rub)
        })
      })

      const planByStudio = new Map<number, {
        grossRevenue: number
        marketingExpenses: number
        marketingLeads: number
        leads: number
        appointments: number
        visitors: number
        regularVisitors: number
        subscriptionVisitors: number
        regularSales: number
        subscriptionSales: number
        regularRevenue: number
        subscriptionRevenue: number
        hasPlanRow: boolean
        hasRegularLine: boolean
        hasSubscriptionLine: boolean
      }>()

      weeklyPlanDetailRows.forEach((row) => {
        const overlapRatio = getRangeOverlapRatio(
          row.week_start,
          row.week_end,
          range.start,
          range.end,
          row.week_len_days
        )
        if (overlapRatio <= 0) return

        const rubMeta = planRubByStudioWeek.get(`${row.studio_internal_id}:${row.week_id}`)
        const fxRateToRub = rubMeta?.fxRateToRub || 1
        const grossRevenueRub = (rubMeta?.grossRevenueRub || 0) * overlapRatio
        const overlapStart = row.week_start > range.start ? row.week_start : range.start
        const overlapEnd = row.week_end < range.end ? row.week_end : range.end
        const overlapDates = getDatesBetween(overlapStart, overlapEnd)
        const weekBaseDays = Math.max(1, row.week_len_days || getInclusiveDays(row.week_start, row.week_end))
        const regularLineRatio = overlapDates.reduce((sum, metricDate) => {
          const type = getEffectiveSubscriptionType(row.studio_internal_id, metricDate)
          return sum + ((type === 'REGULAR' || type === 'MIXED') ? 1 : 0)
        }, 0) / weekBaseDays
        const subscriptionLineRatio = overlapDates.reduce((sum, metricDate) => {
          const type = getEffectiveSubscriptionType(row.studio_internal_id, metricDate)
          return sum + ((type === 'SUBSCRIPTION' || type === 'MIXED') ? 1 : 0)
        }, 0) / weekBaseDays

        const current = planByStudio.get(row.studio_internal_id) || {
          grossRevenue: 0,
          marketingExpenses: 0,
          marketingLeads: 0,
          leads: 0,
          appointments: 0,
          visitors: 0,
          regularVisitors: 0,
          subscriptionVisitors: 0,
          regularSales: 0,
          subscriptionSales: 0,
          regularRevenue: 0,
          subscriptionRevenue: 0,
          hasPlanRow: false,
          hasRegularLine: false,
          hasSubscriptionLine: false
        }

        const hasPlanValues = [
          row.weekly_gross_revenue_plan,
          row.weekly_marketing_budget_plan,
          row.weekly_leads_plan,
          row.weekly_appointments_plan,
          row.weekly_new_visitors_plan,
          row.weekly_new_sales_plan,
          row.weekly_new_sales_amount_plan,
          row.weekly_new_subscriptions_count_plan,
          row.weekly_new_subscriptions_amount_plan,
          row.weekly_membership_fees_new_plan
        ].some((value) => value !== null && value !== undefined)
        if (hasPlanValues) {
          current.hasPlanRow = true
        }

        const visitors = toFiniteNumber(row.weekly_new_visitors_plan) * overlapRatio

        current.grossRevenue += grossRevenueRub
        current.marketingExpenses += toFiniteNumber(row.weekly_marketing_budget_plan) * fxRateToRub * overlapRatio
        current.marketingLeads += toFiniteNumber(row.weekly_leads_plan) * overlapRatio
        current.leads += toFiniteNumber(row.weekly_leads_plan) * overlapRatio
        current.appointments += toFiniteNumber(row.weekly_appointments_plan) * overlapRatio
        current.visitors += visitors

        if (regularLineRatio > 0) {
          current.hasRegularLine = true
          current.regularVisitors += toFiniteNumber(row.weekly_new_visitors_plan) * regularLineRatio
          current.regularSales += toFiniteNumber(row.weekly_new_sales_plan) * regularLineRatio
          current.regularRevenue += toFiniteNumber(row.weekly_new_sales_amount_plan) * fxRateToRub * regularLineRatio
        }

        if (subscriptionLineRatio > 0) {
          current.hasSubscriptionLine = true
          current.subscriptionVisitors += toFiniteNumber(row.weekly_new_visitors_plan) * subscriptionLineRatio
          current.subscriptionSales += toFiniteNumber(row.weekly_new_subscriptions_count_plan) * subscriptionLineRatio
          current.subscriptionRevenue +=
            toFiniteNumber(row.weekly_new_subscriptions_amount_plan) * fxRateToRub * subscriptionLineRatio
        }

        planByStudio.set(row.studio_internal_id, current)
      })

      const selectedRangeDays = Math.max(1, getInclusiveDays(range.start, range.end))
      const selectedRangeElapsedRatio = comparisonDate
        ? Math.min(1, getInclusiveDays(range.start, comparisonDate) / selectedRangeDays)
        : 0

      planByStudio.forEach((studioPlan, studioId) => {
        const geographySegment = geographySegmentByKey.get(getGeographySegmentKey(studioId))
        if (!geographySegment) return

        if (studioPlan.grossRevenue <= 0) return

        geographySegment.planFilledStudios.add(studioId)
        geographySegment.planTotals.marketingExpenses += studioPlan.marketingExpenses
        geographySegment.planTotals.leads += studioPlan.leads
        geographySegment.planTotals.appointments += studioPlan.appointments
        geographySegment.planTotals.visitors += studioPlan.visitors
        geographySegment.planTotals.regularVisitors += studioPlan.regularVisitors
        geographySegment.planTotals.subscriptionVisitors += studioPlan.subscriptionVisitors
        geographySegment.planTotals.regularSales += studioPlan.regularSales
        geographySegment.planTotals.subscriptionSales += studioPlan.subscriptionSales
        geographySegment.planTotals.regularRevenue += studioPlan.regularRevenue
        geographySegment.planTotals.subscriptionRevenue += studioPlan.subscriptionRevenue

        if (studioPlan.marketingExpenses > 0) {
          geographySegment.planMarketingStudios.add(studioId)
          geographySegment.planTotals.marketingLeads += studioPlan.leads
        }

        if (studioPlan.hasRegularLine) {
          geographySegment.planRegularStudios.add(studioId)
        }

        if (studioPlan.hasSubscriptionLine) {
          geographySegment.planSubscriptionStudios.add(studioId)
        }
      })

      const createMetricTotals = (): MarketingMetricTotals => ({
        marketingExpenses: 0,
        marketingLeads: 0,
        leads: 0,
        appointments: 0,
        visitors: 0,
        regularVisitors: 0,
        subscriptionVisitors: 0,
        regularSales: 0,
        subscriptionSales: 0,
        regularRevenue: 0,
        subscriptionRevenue: 0
      })
      const alignedBySegment = new Map<GeographySegmentKey, MarketingAlignedSegment>(
        geographySegmentAccumulators.map((segment) => [segment.key, {
          baseStudios: new Set<number>(),
          regularStudios: new Set<number>(),
          subscriptionStudios: new Set<number>(),
          fact: createMetricTotals(),
          plan: createMetricTotals(),
          leadPaceFact: 0,
          leadPacePlan: 0
        }])
      )

      planByStudio.forEach((studioPlan, studioId) => {
        const factStudio = factByStudio.get(studioId)
        if (!factStudio || !studioPlan.hasPlanRow) return

        const segment = alignedBySegment.get(getGeographySegmentKey(studioId))
        if (!segment) return

        segment.baseStudios.add(studioId)
        const sharedMetrics: Array<keyof MarketingMetricTotals> = [
          'marketingExpenses', 'marketingLeads', 'leads', 'appointments', 'visitors'
        ]
        sharedMetrics.forEach((metric) => {
          segment.fact[metric] += factStudio[metric]
          segment.plan[metric] += studioPlan[metric]
        })

        if (factStudio.hasRegularLine && studioPlan.hasRegularLine) {
          segment.regularStudios.add(studioId)
          segment.fact.regularVisitors += factStudio.regularVisitors
          segment.fact.regularSales += factStudio.regularSales
          segment.fact.regularRevenue += factStudio.regularRevenue
          segment.plan.regularVisitors += studioPlan.regularVisitors
          segment.plan.regularSales += studioPlan.regularSales
          segment.plan.regularRevenue += studioPlan.regularRevenue
        }
        if (factStudio.hasSubscriptionLine && studioPlan.hasSubscriptionLine) {
          segment.subscriptionStudios.add(studioId)
          segment.fact.subscriptionVisitors += factStudio.subscriptionVisitors
          segment.fact.subscriptionSales += factStudio.subscriptionSales
          segment.fact.subscriptionRevenue += factStudio.subscriptionRevenue
          segment.plan.subscriptionVisitors += studioPlan.subscriptionVisitors
          segment.plan.subscriptionSales += studioPlan.subscriptionSales
          segment.plan.subscriptionRevenue += studioPlan.subscriptionRevenue
        }

        if (selectedRangeElapsedRatio > 0 && studioPlan.leads > 0) {
          segment.leadPaceFact += paceLeadsByStudio.get(studioId) || 0
          segment.leadPacePlan += studioPlan.leads * selectedRangeElapsedRatio
        }
      })

      const revenueAverageRangeDays = Math.max(
        1,
        range.rangeDays || getInclusiveDays(range.start, range.end)
      )

      const studioRevenueSegments = segmentAccumulators.map((segment) => {
        const workedStudioDays = segment.studioDays.size
        const excludedStudioDays = segment.excludedStudioDays.size
        const studiosCount = segment.studios.size
        const studiosAtPeriodEnd = segment.endStudios.size

        const averagePerStudioDayRaw =
          workedStudioDays > 0 ? segment.revenue / workedStudioDays : 0

        const averagePerStudioDay = Math.round(averagePerStudioDayRaw)

        const averageWeeklyPerStudio = Math.round(averagePerStudioDayRaw * 7)

        const averagePerStudioForPeriod = Math.round(averagePerStudioDayRaw * revenueAverageRangeDays)
        const cameFrom = getMovementRows(segment.cameFrom)
        const leftTo = getMovementRows(segment.leftTo)
        const cameCount = cameFrom.reduce((sum, row) => sum + row.count, 0)
        const leftCount = leftTo.reduce((sum, row) => sum + row.count, 0)

        return {
          key: segment.key,
          label: segment.label,
          description: segment.description,
          revenue: segment.revenue,
          excludedRevenue: segment.excludedRevenue,
          workedStudioDays,
          excludedStudioDays,
          studiosCount,
          studiosAtPeriodEnd,
          cameCount,
          leftCount,
          cameFrom,
          leftTo,
          averagePerStudioDay,
          averageWeeklyPerStudio,
          averagePerStudioForPeriod
        }
      })
      const geographySegments = geographySegmentAccumulators.map((segment) => {
        const aligned = alignedBySegment.get(segment.key)!
        const totals = aligned.fact
        const planTotals = aligned.plan
        const contributingStudiosCount = aligned.baseStudios.size
        const marketingStudiosCount = aligned.baseStudios.size
        const regularStudiosCount = aligned.regularStudios.size
        const subscriptionStudiosCount = aligned.subscriptionStudios.size
        const planFilledStudiosCount = aligned.baseStudios.size
        const planMarketingStudiosCount = aligned.baseStudios.size
        const planRegularStudiosCount = aligned.regularStudios.size
        const planSubscriptionStudiosCount = aligned.subscriptionStudios.size
        const leadPaceRatio = aligned.leadPacePlan > 0
          ? aligned.leadPaceFact / aligned.leadPacePlan
          : null
        const leadPaceStatus: LeadPaceStatus = leadPaceRatio === null
          ? 'neutral'
          : leadPaceRatio >= 1
            ? 'good'
            : leadPaceRatio >= 0.9
              ? 'warning'
              : 'bad'

        return {
          key: segment.key,
          label: segment.label,
          description: segment.description,
          studiosCount: segment.studioIds.size,
          marketingStudiosCount,
          averageMarketingSpendPerStudio:
            marketingStudiosCount > 0
              ? Math.round(totals.marketingExpenses / marketingStudiosCount)
              : 0,
          costPerLead:
            totals.marketingLeads > 0
              ? Math.round(totals.marketingExpenses / totals.marketingLeads)
              : 0,
          averageLeadsPerStudio:
            contributingStudiosCount > 0
              ? Math.round((totals.leads / contributingStudiosCount) * 10) / 10
              : 0,
          averageAppointmentsPerStudio:
            contributingStudiosCount > 0
              ? Math.round((totals.appointments / contributingStudiosCount) * 10) / 10
              : 0,
          averageVisitorsPerStudio:
            contributingStudiosCount > 0
              ? Math.round((totals.visitors / contributingStudiosCount) * 10) / 10
              : 0,
          regularStudiosCount,
          averageRegularSalesPerStudio:
            regularStudiosCount > 0
              ? Math.round((totals.regularSales / regularStudiosCount) * 10) / 10
              : 0,
          regularSalesConversion:
            totals.regularVisitors > 0
              ? Math.round((totals.regularSales / totals.regularVisitors) * 1000) / 10
              : 0,
          appointmentConversion:
            totals.leads > 0
              ? Math.round((totals.appointments / totals.leads) * 1000) / 10
              : 0,
          visitConversion:
            totals.appointments > 0
              ? Math.round((totals.visitors / totals.appointments) * 1000) / 10
              : 0,
          averageRegularCheck:
            totals.regularSales > 0
              ? Math.round(totals.regularRevenue / totals.regularSales)
              : 0,
          subscriptionStudiosCount,
          averageSubscriptionSalesPerStudio:
            subscriptionStudiosCount > 0
              ? Math.round((totals.subscriptionSales / subscriptionStudiosCount) * 10) / 10
              : 0,
          subscriptionSalesConversion:
            totals.subscriptionVisitors > 0
              ? Math.round((totals.subscriptionSales / totals.subscriptionVisitors) * 1000) / 10
              : 0,
          averageSubscriptionCheck:
            totals.subscriptionSales > 0
              ? Math.round(totals.subscriptionRevenue / totals.subscriptionSales)
              : 0,
          hasSubscriptionBlock: subscriptionStudiosCount > 0 || planSubscriptionStudiosCount > 0,
          planFilledStudiosCount,
          planAverageMarketingSpendPerStudio:
            planMarketingStudiosCount > 0
              ? Math.round(planTotals.marketingExpenses / planMarketingStudiosCount)
              : 0,
          planCostPerLead:
            planTotals.marketingLeads > 0
              ? Math.round(planTotals.marketingExpenses / planTotals.marketingLeads)
              : 0,
          planAverageLeadsPerStudio:
            planFilledStudiosCount > 0
              ? Math.round((planTotals.leads / planFilledStudiosCount) * 10) / 10
              : 0,
          planAverageAppointmentsPerStudio:
            planFilledStudiosCount > 0
              ? Math.round((planTotals.appointments / planFilledStudiosCount) * 10) / 10
              : 0,
          planAverageVisitorsPerStudio:
            planFilledStudiosCount > 0
              ? Math.round((planTotals.visitors / planFilledStudiosCount) * 10) / 10
              : 0,
          planAverageRegularSalesPerStudio:
            planRegularStudiosCount > 0
              ? Math.round((planTotals.regularSales / planRegularStudiosCount) * 10) / 10
              : 0,
          planRegularSalesConversion:
            planTotals.regularVisitors > 0
              ? Math.round((planTotals.regularSales / planTotals.regularVisitors) * 1000) / 10
              : 0,
          planAppointmentConversion:
            planTotals.leads > 0
              ? Math.round((planTotals.appointments / planTotals.leads) * 1000) / 10
              : 0,
          planVisitConversion:
            planTotals.appointments > 0
              ? Math.round((planTotals.visitors / planTotals.appointments) * 1000) / 10
              : 0,
          planAverageRegularCheck:
            planTotals.regularSales > 0
              ? Math.round(planTotals.regularRevenue / planTotals.regularSales)
              : 0,
          planAverageSubscriptionSalesPerStudio:
            planSubscriptionStudiosCount > 0
              ? Math.round((planTotals.subscriptionSales / planSubscriptionStudiosCount) * 10) / 10
              : 0,
          planSubscriptionSalesConversion:
            planTotals.subscriptionVisitors > 0
              ? Math.round((planTotals.subscriptionSales / planTotals.subscriptionVisitors) * 1000) / 10
              : 0,
          planAverageSubscriptionCheck:
            planTotals.subscriptionSales > 0
              ? Math.round(planTotals.subscriptionRevenue / planTotals.subscriptionSales)
              : 0,
          leadPaceStatus
        }
      })

      const crmFactRaw = crmData.reduce(
        (sum, row) => sum + toFiniteNumber(row.amount_rub),
        0
      )
      const crmFact = crmData.length > 0 ? crmFactRaw : null
      const currentFact = appFact
      const totalRemaining = Math.max(partnerPlan - currentFact, 0)
      const elapsedDays = getElapsedDaysInRange(range.start, range.end, todayIso)
      const elapsedDaysToDate = comparisonDate
        ? getElapsedDaysInRange(range.start, range.end, comparisonDate)
        : 0
      const rangeDays = Math.max(1, range.rangeDays || getInclusiveDays(range.start, range.end))
      const daysRemaining = getRemainingDaysInRange(range.start, range.end, todayIso)
      const daysForAverage = elapsedDays > 0 ? elapsedDays : rangeDays
      const weeksForAverage = Math.max(1 / 7, daysForAverage / 7)
      const allRevenueSegment = studioRevenueSegments.find((segment) => segment.key === 'all')
      const averageWeeklyRevenuePerStudio = allRevenueSegment && allRevenueSegment.workedStudioDays > 0
        ? allRevenueSegment.averageWeeklyPerStudio
        : elapsedWorkingStudios.size > 0
          ? Math.round(currentFact / weeksForAverage / elapsedWorkingStudios.size)
          : 0
      const workedStudioDays = allRevenueSegment?.workedStudioDays || 0
      const planProgress = partnerPlan > 0 ? Math.round((currentFact / partnerPlan) * 1000) / 10 : 0
      const planByDate = Math.min(100, Math.round((Math.max(0, elapsedDays) / rangeDays) * 1000) / 10)
      const planToDateRatio = comparisonDate
        ? Math.min(1, Math.max(0, elapsedDaysToDate / rangeDays))
        : 0
      const planToDate = Math.round(planToDateRatio * 1000) / 10
      const appFactToDate = comparisonDate
        ? rangeRevenueData
            .filter((row) => row.metric_date <= comparisonDate)
            .reduce((sum, row) => sum + toFiniteNumber(row.gross_revenue_rub), 0)
        : 0
      const crmFactToDateRaw = comparisonDate
        ? crmData
            .filter((row) => row.date && row.date <= comparisonDate)
            .reduce((sum, row) => sum + toFiniteNumber(row.amount_rub), 0)
        : 0
      const crmFactToDate = crmData.length > 0 ? crmFactToDateRaw : null
      const crmYesterdayRevenue = rangeState === 'current'
        ? crmData
            .filter((row) => row.date === yesterdayStr)
            .reduce((sum, row) => sum + toFiniteNumber(row.amount_rub), 0)
        : 0
      const crmTodayRevenue = rangeState === 'current'
        ? crmData
            .filter((row) => row.date === todayIso)
            .reduce((sum, row) => sum + toFiniteNumber(row.amount_rub), 0)
        : 0
      const expectedFactByDate = partnerPlan * planToDateRatio
      const difference = appFactToDate - expectedFactByDate
      const crmDifference = crmFact === null ? null : currentFact - crmFact

      const todayRevenue = ((todayRevenueResult.data || []) as Array<{
        studio_internal_id: number
        metric_date: string
        gross_revenue_rub: number | null
      }>).reduce((sum, record) =>
        sum + toFiniteNumber(record.gross_revenue_rub), 0)

      const yesterdayRevenue = ((yesterdayRevenueResult.data || []) as Array<{
        studio_internal_id: number
        metric_date: string
        gross_revenue_rub: number | null
      }>).reduce((sum, record) =>
        sum + toFiniteNumber(record.gross_revenue_rub), 0)

      const latestClientsByStudio = new Map<number, { date: string, value: number }>()
      const firstClientsByStudio = new Map<number, { date: string, value: number }>()
      const latestSubscribersByStudio = new Map<number, { date: string, value: number }>()
      const firstSubscribersByStudio = new Map<number, { date: string, value: number }>()
      ;((clientsRows.data || []) as Array<{
        studio_internal_id: number
        metric_date: string
        total_clients_count: number | null
        active_subscribers_count: number | null
      }>).forEach((row) => {
        // Не фильтруем по статусу студии: клиент числится по студии последнего платежа
        // в CRM, а не по её текущему статусу. Когда студия закрывается, реальные клиенты,
        // купившие абонемент там и продолжающие ходить в другую открытую студию того же
        // франчайзи, остаются привязаны к номеру закрытой — это не мусор, а лаг переноса
        // в CRM. Поэтому сеть считаем по всем студиям, как это делает activeclients_etl_pipeline.
        if (row.total_clients_count !== null && row.total_clients_count !== undefined) {
          const value = toFiniteNumber(row.total_clients_count)
          const latest = latestClientsByStudio.get(row.studio_internal_id)
          if (!latest || row.metric_date > latest.date) {
            latestClientsByStudio.set(row.studio_internal_id, { date: row.metric_date, value })
          }
          const first = firstClientsByStudio.get(row.studio_internal_id)
          if (!first || row.metric_date < first.date) {
            firstClientsByStudio.set(row.studio_internal_id, { date: row.metric_date, value })
          }
        }

        if (row.active_subscribers_count !== null && row.active_subscribers_count !== undefined) {
          const value = toFiniteNumber(row.active_subscribers_count)
          const latest = latestSubscribersByStudio.get(row.studio_internal_id)
          if (!latest || row.metric_date > latest.date) {
            latestSubscribersByStudio.set(row.studio_internal_id, { date: row.metric_date, value })
          }
          const first = firstSubscribersByStudio.get(row.studio_internal_id)
          if (!first || row.metric_date < first.date) {
            firstSubscribersByStudio.set(row.studio_internal_id, { date: row.metric_date, value })
          }
        }
      })

      const totalClients = Array.from(latestClientsByStudio.values()).reduce((sum, row) => sum + row.value, 0)
      // Считаем прирост по всем студиям, попавшим в latest/first-карты (вся сеть), а не только
      // по dashboardStudioIds — иначе прирост считался бы по урезанной подсети, а итог по сети.
      const clientsGrowth = Array.from(latestClientsByStudio.keys()).reduce((sum, studioId) => {
        const latest = latestClientsByStudio.get(studioId)?.value
        const first = firstClientsByStudio.get(studioId)?.value
        if (latest === undefined || first === undefined) return sum
        return sum + (latest - first)
      }, 0)
      const totalSubscribers = Array.from(latestSubscribersByStudio.values()).reduce((sum, row) => sum + row.value, 0)
      const subscribersGrowth = Array.from(latestSubscribersByStudio.keys()).reduce((sum, studioId) => {
        const latest = latestSubscribersByStudio.get(studioId)?.value
        const first = firstSubscribersByStudio.get(studioId)?.value
        if (latest === undefined || first === undefined) return sum
        return sum + (latest - first)
      }, 0)

      const overview = {
        totalStudios: summaryWorkingStudios.size,
        studiosWithoutYesterday: studiosWithoutYesterdayData,
        studiosWithoutConfirmations: studiosWithoutConfirmations,
        studiosWithoutPlan: studiosWithoutPlan,
        avgCompletionRate: completionRates.length > 0
          ? Math.round(completionRates.reduce((sum, value) => sum + value, 0) / completionRates.length)
          : 0
      }

      const planFact = {
        partnerPlan,
        managementPlan,
        managementPlanIsPlaceholder: false,
        appFact,
        crmFact,
        currentFact,
        planProgress,
        planByDate,
        difference,
        crmDifference,
        averageWeeklyRevenuePerStudio,
        workedStudioDays
      }

      const clients = {
        totalClients,
        monthlyGrowth: clientsGrowth
      }

      const subscribers = {
        totalSubscribers,
        subscribersGrowth
      }

      const revenue = {
        today: todayRevenue,
        yesterday: yesterdayRevenue
      }

      const networkPerDay = daysRemaining > 0 ? Math.round(totalRemaining / daysRemaining) : 0
      const perStudio = openStudiosCount > 0 ? Math.round(totalRemaining / openStudiosCount) : 0
      const perDay = openStudiosCount > 0 ? Math.round(networkPerDay / openStudiosCount) : 0

      const remainingPlan = {
        totalRemaining,
        perStudio,
        perDay,
        networkPerDay,
        openStudiosCount,
        daysRemaining
      }

      const recentActivity: ActivityItem[] = analytics
        .filter(s => s.last_entry_date)
        .sort((a, b) => new Date(b.last_entry_date!).getTime() - new Date(a.last_entry_date!).getTime())
        .slice(0, 5)
        .map(studio => ({
          studioId: studio.studio_internal_id,
          studioName: studio.studio_number,
          activityType: 'data_filled' as const,
          timestamp: studio.last_entry_date!,
          details: `Заполнено ${studio.completion_percentage}% дней месяца`
        }))

      const monthlyTrends: TrendData[] = [{
        date: `${currentPeriod.year_number}-${String(currentPeriod.month_number).padStart(2, '0')}`,
        completionRate: overview.avgCompletionRate,
        confirmationRate: confirmationRates.length > 0
          ? Math.round(confirmationRates.reduce((sum, value) => sum + value, 0) / confirmationRates.length)
          : 0,
        totalStudios: overview.totalStudios
      }]

      const dashboardData: ManagementDashboardData = {
        overview,
        planFact,
        studioRevenueSegments,
        geographySegments,
        clients,
        revenue,
        remainingPlan,
        recentActivity,
        problemStudios: problems,
        monthlyTrends,
        subscribers,
        appFactToDate,
        crmFactToDate,
        crmYesterdayRevenue,
        crmTodayRevenue,
        planToDate,
        planToDateRatio,
        rangeState
      }

      setDashboardData(dashboardData)
      console.log('✅ Dashboard data loaded successfully')
    } catch (err) {
      console.error('❌ Error loading dashboard data:', err)
      setError(err instanceof Error ? err.message : 'Ошибка загрузки данных')
    } finally {
      setLoading(false)
    }
  }

  const visibleRevenueSegments = useMemo(() => {
    if (!dashboardData?.studioRevenueSegments.length) return []
    return dashboardData.studioRevenueSegments.filter((segment) => segment.key !== 'all')
  }, [dashboardData])

  const maxSegmentRevenue = useMemo(() => {
    if (!visibleRevenueSegments.length) return 0
    return Math.max(...visibleRevenueSegments.map((segment) => segment.averagePerStudioForPeriod))
  }, [visibleRevenueSegments])

  const getSegmentIcon = (key: RevenueSegmentKey) => {
    switch (key) {
      case 'foreign':
        return <Globe2 className="w-4 h-4 text-sky-600" />
      case 'subscription':
        return <Repeat2 className="w-4 h-4 text-violet-600" />
      case 'mixed':
        return <Activity className="w-4 h-4 text-fuchsia-600" />
      case 'regular':
        return <WalletCards className="w-4 h-4 text-emerald-600" />
      default:
        return <Activity className="w-4 h-4 text-blue-600" />
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="text-center border border-slate-200 bg-card shadow-sm">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium mb-2">Ошибка загрузки</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Попробовать снова
          </button>
        </Card>
      </div>
    )
  }

  if (!dashboardData) {
    return <div>Нет данных</div>
  }

  const managementPlan = toFiniteNumber(dashboardData.planFact.managementPlan)
  const crmFact = dashboardData.planFact.crmFact === null ? 0 : toFiniteNumber(dashboardData.planFact.crmFact)
  const crmFactToDate = dashboardData.crmFactToDate === null ? 0 : toFiniteNumber(dashboardData.crmFactToDate)
  const canEditUkPlan = managementAccessLevel === 'advanced'
  const hasNetworkClientSnapshot = networkKpis?.clientTotal != null && networkKpis.clientDelta28d != null
  const clientTotal = hasNetworkClientSnapshot ? networkKpis.clientTotal : dashboardData.clients.totalClients
  const clientDelta = hasNetworkClientSnapshot ? networkKpis.clientDelta28d : dashboardData.clients.monthlyGrowth
  const clientSnapshotDate = hasNetworkClientSnapshot && networkKpis.clientSnapshotDate
    ? new Date(`${networkKpis.clientSnapshotDate}T00:00:00`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    : null

  return (
    <div className="space-y-6 bg-slate-50/50">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2 text-slate-950">Дашборд управляющей компании (в разработке)</h1>
      </div>

      <Card className="border border-slate-200 bg-card py-2 shadow-sm">
        <PeriodSelector
          currentPeriod={periods.currentPeriod}
          onPrevPeriod={periods.goToPrevPeriod}
          onNextPeriod={periods.goToNextPeriod}
          canGoPrev={periods.canGoPrev}
          canGoNext={periods.canGoNext}
          showViewModeSelector
          isCurrentPeriod={periods.isCurrentPeriod}
          periodFilterValue={selectedTimeFilter}
          periodFilterOptions={timeFilterOptions}
          onPeriodFilterChange={setSelectedTimeFilter}
          selectedRange={selectedRange}
          compact
        />
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <PlanFactBlock
          title="План УК и факт Listok"
          planLabel="План УК"
          factLabel="Факт Listok"
          plan={managementPlan}
          fact={crmFact}
          factToDate={crmFactToDate}
          planToDatePercent={dashboardData.planToDate}
          planToDateRatio={dashboardData.planToDateRatio}
          yesterdayRevenue={dashboardData.crmYesterdayRevenue}
          todayRevenue={dashboardData.crmTodayRevenue}
          daysRemaining={dashboardData.remainingPlan.daysRemaining}
          activeStudiosForDay={dashboardData.remainingPlan.openStudiosCount}
          planLabelAction={canEditUkPlan ? (
            <button
              type="button"
              onClick={() => setIsUkPlanModalOpen(true)}
              className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              aria-label="Редактировать план УК"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          ) : undefined}
          tone="gold"
          rangeState={dashboardData.rangeState}
        />

        <PlanFactBlock
          title="План и факт приложения"
          planLabel="План франчайзи"
          factLabel="Факт приложения"
          plan={dashboardData.planFact.partnerPlan}
          fact={dashboardData.planFact.appFact}
          factToDate={dashboardData.appFactToDate}
          planToDatePercent={dashboardData.planToDate}
          planToDateRatio={dashboardData.planToDateRatio}
          yesterdayRevenue={dashboardData.revenue.yesterday}
          todayRevenue={dashboardData.revenue.today}
          daysRemaining={dashboardData.remainingPlan.daysRemaining}
          activeStudiosForDay={dashboardData.remainingPlan.openStudiosCount}
          tone="gray"
          rangeState={dashboardData.rangeState}
        />
      </div>

      <RoyaltyNetworkCard
        data={networkKpis}
        isUnavailable={networkKpisUnavailable || (networkKpis !== null && networkKpis.royaltyMonth === null)}
      />

      <UkWeeklyPlanModal
        isOpen={isUkPlanModalOpen}
        onClose={() => setIsUkPlanModalOpen(false)}
        periodId={periods.currentPeriod?.period_id ?? null}
        weeks={ukPlanWeeks}
        userId={user?.id}
        onSaved={loadDashboardData}
      />

      <Card className="border border-slate-200 bg-card shadow-sm">
        <div className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Сводка сети</h2>
          </div>

          <div className={networkFixedCd ? 'grid gap-1.5 xl:grid-cols-[minmax(0,3fr)_minmax(280px,1fr)]' : ''}>
            <div className={`grid gap-1.5 sm:grid-cols-2 ${networkFixedCd ? 'xl:grid-cols-3' : 'xl:grid-cols-4'}`}>
              <MiniValue
                label="Студии"
                value={dashboardData.overview.totalStudios.toLocaleString('ru-RU')}
                icon={<Building2 className="h-4 w-4 text-slate-500" />}
              />
              <MiniValue
                label={clientSnapshotDate ? (
                  <span className="inline-flex items-center gap-1 normal-case tracking-normal">
                    <span>Клиентская база · {clientSnapshotDate}</span>
                    <span className="relative inline-flex items-center group cursor-help">
                      <button
                        type="button"
                        className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        aria-label="Как считается изменение клиентской базы за 28 дней"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                      <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-64 whitespace-normal rounded-lg bg-slate-950 px-3 py-2.5 text-left text-xs font-normal leading-snug text-white shadow-xl group-hover:block group-focus-within:block">
                        Клиентская база на дату снимка. Цветная цифра — рост или снижение по сравнению с 28 днями ранее.
                      </span>
                    </span>
                  </span>
                ) : 'Клиенты'}
                icon={<Users className="h-4 w-4 text-emerald-700" />}
                value={(
                  <span className="inline-flex items-baseline gap-2">
                    <span>{clientTotal.toLocaleString('ru-RU')}</span>
                    <span className={`text-base ${clientDelta >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {clientDelta >= 0 ? '+' : ''}{clientDelta.toLocaleString('ru-RU')}
                    </span>
                    {hasNetworkClientSnapshot ? <span className="text-xs font-medium text-slate-400">за 28 дн.</span> : null}
                  </span>
                )}
              />
              <MiniValue
                label="Подписчики"
                icon={<Gauge className="h-4 w-4 text-blue-700" />}
                value={(
                  <span className="inline-flex items-baseline gap-2">
                    <span>{dashboardData.subscribers.totalSubscribers.toLocaleString('ru-RU')}</span>
                    <span className={`text-base ${dashboardData.subscribers.subscribersGrowth >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {dashboardData.subscribers.subscribersGrowth >= 0 ? '+' : ''}{dashboardData.subscribers.subscribersGrowth.toLocaleString('ru-RU')}
                    </span>
                  </span>
                )}
              />
              <MiniValue
                label={dashboardData.rangeState === 'current' ? 'Не заполнили за вчера' : 'Не заполнили за день'}
                value={dashboardData.overview.studiosWithoutYesterday.toLocaleString('ru-RU')}
                icon={<Calendar className="h-4 w-4 text-slate-500" />}
              />
              <MiniValue
                label="Нет подтверждения > 2 дн."
                value={dashboardData.overview.studiosWithoutConfirmations.toLocaleString('ru-RU')}
                icon={<CheckCircle className="h-4 w-4 text-slate-500" />}
                tone={dashboardData.overview.studiosWithoutConfirmations > 0 ? 'bad' : 'good'}
              />
              <MiniValue
                label="Без плана"
                value={dashboardData.overview.studiosWithoutPlan.toLocaleString('ru-RU')}
                icon={<FileText className="h-4 w-4 text-slate-500" />}
                tone={dashboardData.overview.studiosWithoutPlan > 0 ? 'bad' : 'good'}
              />
              <MiniValue
                label="Качество тренировок"
                icon={<Activity className="h-4 w-4 text-violet-700" />}
                value={networkKpis ? (
                  <span className="inline-flex items-baseline gap-2">
                    <span>{formatNullablePercent(networkKpis.qualityAveragePercent)}</span>
                    <span className="text-base text-slate-500">{formatNullableCount(networkKpis.qualityReviewsCount)} отсм.</span>
                  </span>
                ) : '—'}
              />
              <MiniValue
                label="Первичные записи на 7 дн."
                value="В очереди"
                size="sm"
                icon={<Calendar className="h-4 w-4 text-slate-500" />}
              />
            </div>

            {networkFixedCd ? (
              <NetworkFixedCdDashboardCard snapshot={networkFixedCd} />
            ) : null}
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 bg-card shadow-sm">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Выручка на студию</h2>
            </div>
            <div className="text-right text-sm">
              <div className="text-slate-500">Средняя недельная по сети</div>
              <div className="font-semibold text-slate-950">
                {formatFullCurrency(dashboardData.planFact.averageWeeklyRevenuePerStudio)}
              </div>
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-[1180px] w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-[0.08em] text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Сегмент</th>
                    <th className="px-4 py-3 text-right font-medium">Студий на конец периода</th>
                    <th className="px-4 py-3 text-right font-medium">Студия-дни в статусе OPEN</th>
                    <th className="px-4 py-3 text-right font-medium">Студия-дни в других статусах</th>
                    <th className="px-4 py-3 text-right font-medium">Выручка OPEN</th>
                    <th className="px-4 py-3 text-right font-medium">Выручка другие</th>
                    <th className="px-4 py-3 text-right font-medium">Средняя в день</th>
                    <th className="px-4 py-3 text-right font-medium">Средняя за период</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {visibleRevenueSegments.map((segment) => (
                    <tr key={segment.key}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 font-semibold text-slate-950">
                          {getSegmentIcon(segment.key)}
                          {segment.label}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{segment.description}</div>
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        <RevenueStudioCountCell segment={segment} />
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        {segment.workedStudioDays.toLocaleString('ru-RU')}
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        {segment.excludedStudioDays.toLocaleString('ru-RU')}
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        {formatFullCurrency(segment.revenue)}
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        {formatFullCurrency(segment.excludedRevenue)}
                      </td>
                      <td className="px-4 py-4 text-right text-slate-950 whitespace-nowrap">
                        {formatFullCurrency(segment.averagePerStudioDay)}
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-slate-950 whitespace-nowrap">
                        {formatFullCurrency(segment.averagePerStudioForPeriod)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="text-sm font-semibold text-slate-950">Диаграмма по сегментам</div>
              <div className="mt-1 text-xs text-slate-500">Средняя выручка на студию за период</div>

              <div className="mt-5 space-y-5">
                {visibleRevenueSegments.map((segment) => {
                  const barWidth = maxSegmentRevenue > 0
                    ? Math.max(6, Math.round((segment.averagePerStudioForPeriod / maxSegmentRevenue) * 100))
                    : 0

                  return (
                    <div key={segment.key} className="space-y-2">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <div className="flex items-center gap-2 font-medium text-slate-800">
                          {getSegmentIcon(segment.key)}
                          {segment.label}
                        </div>
                        <div className="font-semibold text-slate-950 whitespace-nowrap">
                          {formatFullCurrency(segment.averagePerStudioForPeriod)}
                        </div>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-card ring-1 ring-slate-200">
                        <div
                          className="h-full rounded-full bg-[#F2C94C]"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 bg-card shadow-sm">
        <div className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Маркетинг по регионам</h2>
              <p className="mt-1 text-sm text-slate-500">
                Факт считается только по дням с ручным сохранением и в текущем периоде — по вчерашний день. Средние и сравнение с планом — только по студиям, где заполнены и план, и факт. CPL и конверсии — по сумме этих студий. В колонках продаж верхнее значение - абонементы, нижнее -
                <span className="font-medium text-amber-700"> подписки</span>.
              </p>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/management/ads-account-mappings')}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-slate-300 p-0 leading-none text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              title="Сопоставление кабинетов"
              aria-label="Сопоставление кабинетов"
            >
              <Link2 className="block h-3.5 w-3.5 shrink-0" />
            </Button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-[1440px] w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Категория</th>
                  <th className="px-4 py-3 text-right font-medium">Студий</th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Расход"
                      hint="Средний расход только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="CPL"
                      hint="Общий расход / общие лиды только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Лиды"
                      hint={(
                        <>
                          Среднее только по студиям, где заполнены план и факт. Цвет факта показывает темп по вчерашний день относительно пропорционального плана выбранного периода или недели:{' '}
                          <span className="font-semibold text-emerald-400">зелёный</span> — 100% и выше,{' '}
                          <span className="font-semibold text-amber-300">жёлтый</span> — 90–99,9%,{' '}
                          <span className="font-semibold text-red-400">красный</span> — ниже 90%.
                        </>
                      )}
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="CR1"
                      hint="Записи / лиды только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Записи"
                      hint="Среднее только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="CR2"
                      hint="Приходы / записи только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Приходы"
                      hint="Среднее только по студиям, где заполнены план и факт."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="CR3"
                      hint="Продажи / приходы только по студиям, где заполнены план и факт. Верхняя строка — абонементы, нижняя — подписки."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Продажи"
                      hint="Среднее только по студиям, где заполнены план и факт. Верхняя строка — абонементы, нижняя — подписки."
                    />
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <TableMetricHint
                      label="Ср. чек"
                      hint="Выручка / продажи только по студиям, где заполнены план и факт. Верхняя строка — абонементы, нижняя — подписки."
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {dashboardData.geographySegments.map((segment) => {
                  return (
                    <tr key={segment.key}>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-semibold text-slate-950">{segment.label}</div>
                        <div className="mt-1 text-xs text-slate-500">{segment.description}</div>
                      </td>
                      <td className="px-4 py-3 align-middle text-right text-slate-950 whitespace-nowrap">
                        {segment.studiosCount.toLocaleString('ru-RU')}
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={segment.marketingStudiosCount > 0 ? formatFullCurrency(segment.averageMarketingSpendPerStudio) : '—'}
                          plan={segment.planFilledStudiosCount > 0 ? formatFullCurrency(segment.planAverageMarketingSpendPerStudio) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={segment.costPerLead > 0 ? formatFullCurrency(segment.costPerLead) : '—'}
                          plan={segment.planCostPerLead > 0 ? formatFullCurrency(segment.planCostPerLead) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={(
                            <span className={{
                              good: 'text-emerald-700',
                              warning: 'text-amber-600',
                              bad: 'text-red-600',
                              neutral: 'text-slate-950'
                            }[segment.leadPaceStatus]}>
                              {formatMetricAverage(segment.averageLeadsPerStudio)}
                            </span>
                          )}
                          plan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageLeadsPerStudio) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={formatPercent(segment.appointmentConversion)}
                          plan={segment.planFilledStudiosCount > 0 ? formatPercent(segment.planAppointmentConversion) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={formatMetricAverage(segment.averageAppointmentsPerStudio)}
                          plan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageAppointmentsPerStudio) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={formatPercent(segment.visitConversion)}
                          plan={segment.planFilledStudiosCount > 0 ? formatPercent(segment.planVisitConversion) : '—'}
                        />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <FactPlanText
                          fact={formatMetricAverage(segment.averageVisitorsPerStudio)}
                          plan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageVisitorsPerStudio) : '—'}
                        />
                      </td>
                      <td className="px-3 py-3 align-middle text-right">
                        {segment.hasSubscriptionBlock ? (
                          <div className="min-w-[92px]">
                            <SplitFactPlanText
                              regularFact={formatPercent(segment.regularSalesConversion)}
                              regularPlan={segment.planFilledStudiosCount > 0 ? formatPercent(segment.planRegularSalesConversion) : '—'}
                              subscriptionFact={formatPercent(segment.subscriptionSalesConversion)}
                              subscriptionPlan={segment.planFilledStudiosCount > 0 ? formatPercent(segment.planSubscriptionSalesConversion) : '—'}
                            />
                          </div>
                        ) : (
                          <div className="min-w-[92px]">
                            <FactPlanText
                              fact={formatPercent(segment.regularSalesConversion)}
                              plan={segment.planFilledStudiosCount > 0 ? formatPercent(segment.planRegularSalesConversion) : '—'}
                            />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-middle text-right">
                        {segment.hasSubscriptionBlock ? (
                          <div className="min-w-[92px]">
                            <SplitFactPlanText
                              regularFact={formatMetricAverage(segment.averageRegularSalesPerStudio)}
                              regularPlan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageRegularSalesPerStudio) : '—'}
                              subscriptionFact={formatMetricAverage(segment.averageSubscriptionSalesPerStudio)}
                              subscriptionPlan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageSubscriptionSalesPerStudio) : '—'}
                            />
                          </div>
                        ) : (
                          <div className="min-w-[92px]">
                            <FactPlanText
                              fact={formatMetricAverage(segment.averageRegularSalesPerStudio)}
                              plan={segment.planFilledStudiosCount > 0 ? formatMetricAverage(segment.planAverageRegularSalesPerStudio) : '—'}
                            />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-middle text-right">
                        {segment.hasSubscriptionBlock ? (
                          <div className="min-w-[112px]">
                            <SplitFactPlanText
                              regularFact={segment.averageRegularCheck > 0 ? formatFullCurrency(segment.averageRegularCheck) : '—'}
                              regularPlan={segment.planAverageRegularCheck > 0 ? formatFullCurrency(segment.planAverageRegularCheck) : '—'}
                              subscriptionFact={segment.averageSubscriptionCheck > 0 ? formatFullCurrency(segment.averageSubscriptionCheck) : '—'}
                              subscriptionPlan={segment.planAverageSubscriptionCheck > 0 ? formatFullCurrency(segment.planAverageSubscriptionCheck) : '—'}
                            />
                          </div>
                        ) : (
                          <div className="min-w-[112px]">
                            <FactPlanText
                              fact={segment.averageRegularCheck > 0 ? formatFullCurrency(segment.averageRegularCheck) : '—'}
                              plan={segment.planAverageRegularCheck > 0 ? formatFullCurrency(segment.planAverageRegularCheck) : '—'}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div className="mt-8">
        <div className="h-20" />
      </div>

      <ManagementCompanyNavigation />
    </div>
  )
}
